import { useCallback, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChatAgentType,
  AGENT_CONFIGS,
  SOCKET_MESSAGE_VERSION,
  type SocketMessageMeta,
} from '@constants/chat-agents';
import type { ChatMessage } from '../types';
import type { ChatComponentItem } from '../types/chat-component';
import { buildComponentAttachments } from '../types/chat-component';
import { useChatAgent } from './useChatAgent';
import { useChatHistory } from './useChatHistory';
import { useChatWebSocket, ConnectionStatus } from './useChatWebSocket';
import { useChatInput } from './useChatInput';
import { useChatMessage } from './useChatMessage';
import { useChatToken } from './useChatToken';
import { useGetUserInfo, type UserInfo } from './useGetUserInfo';
import { useInitialHistoryLoader } from './useInitialHistoryLoader';
import {
  useVoiceInteractionController,
  VOICE_CAPTURE_MAX_DURATION_MS,
  type MicDialogType,
} from './useVoiceInteractionController';
import type { VoicePermissionErrorType } from '@pages/chatbot/components/VoicePermissionModal';
import { useAudioSettings } from './useAudioSettings';
import { useChatSuggestions } from './useChatSuggestions';
import { useAbortableRequest } from './useAbortableRequest';
import { useGeoLocation } from './useGeoLocation';
import { chatService, setMessageSessionId, getMessageSessionId } from '@services/chatService';
import { SenderType, type ChatMessageResponse } from '../types/chat-message';
import { createChatMessageFromUI, convertToChatMessage, alignBlockContentsWithFullContent, isSimilarId, forwardOverrideRegistry } from '@utils/chat-message';
import { extractDirectPdfUrl, extractPdfRedirectData } from '@utils/command';
import { validateFile, resizeImage, MAX_IMAGES_COUNT } from '@utils/fileUpload';
import { generateUUID } from '@utils/uuid';
import { clearChatStorage } from '@utils/chat-storage';
import { safePostMessage } from '@utils/index';
import { stripMarkdown } from '@utils/textUtils';
import { ONBOARDING_FEATURE_ACTIONS } from '@constants/onboardingFeatures';
import { PostMessageType } from '@constants/postMessage';
import {
  actionTracker,
  ActionMacroFeature,
  ActionView,
  ActionType,
  ActionElement,
} from '../services/actionTracker';

const getAgentTag = (agent?: ChatAgentType | null): string => {
  switch (agent) {
    case ChatAgentType.TAX_QUESTION:
      return 'Thuế';
    case ChatAgentType.ANALYTICS_QUESTION:
      return 'Báo cáo';
    case ChatAgentType.INSTRUCTION_MANUAL:
      return 'Hướng dẫn';
    case ChatAgentType.CSKH:
      return 'CSKH';
    case ChatAgentType.AI:
    default:
      return 'Hỏi đáp';
  }
};

const getAgentTabElement = (agent?: ChatAgentType | null): string => {
  switch (agent) {
    case ChatAgentType.TAX_QUESTION:
      return ActionElement.TAB_TAX;
    case ChatAgentType.ANALYTICS_QUESTION:
      return ActionElement.TAB_REPORT;
    case ChatAgentType.INSTRUCTION_MANUAL:
      return ActionElement.TAB_GUIDE;
    case ChatAgentType.CSKH:
      return ActionElement.TAB_CSKH;
    case ChatAgentType.AI:
    default:
      return ActionElement.TAB_AI;
  }
};

/** Server echo for USER: merge blockContents + flattened text so ChatUser can render type IMAGES */
const buildUserEchoMergeUpdates = (chatMessage: ChatMessage): Partial<ChatMessage> => {
  const merge: Partial<ChatMessage> = {};
  const blocks = chatMessage.blockContents;
  if (blocks && blocks.length > 0) {
    merge.blockContents = blocks;
  }
  // Never overwrite optimistic text with empty strings from the server (fixes blank bubble until history reload)
  if (typeof chatMessage.text === 'string' && chatMessage.text.trim()) {
    merge.text = chatMessage.text;
  }
  if (typeof chatMessage.fullContent === 'string' && chatMessage.fullContent.trim()) {
    merge.fullContent = chatMessage.fullContent;
  }

  return merge;
};


// Interface cho PDF item trong redirect command
export interface PdfItem {
  title: string;
  uri: string;
}

// Interface cho redirect data
export interface RedirectData {
  type: string; // 'tax_handbook' | 'lump_tax'
  items: PdfItem[];
  commandName: string;
}

export type ToastMessage = {
  text: string;
  type: 'success' | 'error' | 'info' | 'cskh';
  id?: string;
  title?: string;
  timeLabel?: string;
} | null;

interface UseChatReturn {
  // Agent
  activeAgent: ChatAgentType;
  handleAgentChange: (newAgent: ChatAgentType) => void;

  // Chat History
  currentChatLog: ChatMessage[];
  currentHasChatHistory: boolean | null;
  connected: boolean;
  loadMoreHistory: () => Promise<void>;
  loadNewerHistory: () => Promise<void>;
  isFetchingMore: boolean;

  // Input
  inputValue: string;
  listening: boolean;
  onChangeInput: (value: string) => void;
  handleMicrophoneClick: () => void;
  stopMicAndDiscard: () => void;
  pauseMicAndKeepText: () => void;
  cancelMicAndClearText: () => void;
  voiceErrorModalType: VoicePermissionErrorType | null;
  closeVoiceErrorModal: () => void;
  retryVoicePermission: () => void;
  isChatInputDisabled: boolean;

  // Messages
  handleChatSubmit: (
    message: string,
    buttonId?: number,
    isHidden?: boolean,
    images?: Array<{ contentType: string; imageUrl: string; name: string; uploadStatus: string }>,
    component?: import('../types/chat-component').ChatComponentItem | null,
    meta?: SocketMessageMeta,
  ) => void | Promise<void>;
  handleCommandClick: (
    command: string,
    id?: number,
    options?: { actionType?: string; actionValue?: string },
  ) => void;
  commands: import('../types/chat-command').DefaultCommand[];
  isCommandsLoading: boolean;
  handleSwitchAgent: () => void;
  handleSkipSwitch: () => void;
  switchNotification: any;
  isThinking: boolean;
  setIsThinking: (value: boolean) => void;

  // Redirect command
  redirectData: RedirectData | null;
  closeRedirectOverlay: () => void;

  // Quick Selection
  quickFunctions: any[];
  quickFunctionSearchList: any[];
  instructions: any[];
  isInstructionsLoading: boolean;
  refreshInstructions: () => Promise<void>;
  quickSelectionData: { title: string; items: any[]; type: 'INSTRUCTION' | 'QUICK_FUNCTION'; isLoading?: boolean } | null;
  handleInstructionClick: () => void | Promise<void>;
  handleQuickFunctionClick: () => void | Promise<void>;
  closeQuickSelection: () => void;
  handleQuickSelectionSelect: (item: any) => void;

  // Refs
  chatRef: React.RefObject<HTMLDivElement | null>;

  // Config
  agentConfig: any;
  onReadyConversation: () => void;
  isSTTSupported: boolean;
  micDialog: MicDialogType | null;
  closeMicDialog: () => void;
  confirmMicDialog: () => void;
  handleForwardToAgent: (targetAgent: ChatAgentType, userQuestion: string | undefined, botMessage: ChatMessage | undefined, botMessageId?: string) => void;

  // Audio
  isAudioEnabled: boolean;
  toggleAudio: () => void;
  setAudioEnabled: (enabled: boolean) => void;
  isBotSpeaking: boolean;
  isAudioPaused: boolean;
  isAudioPlayerVisible: boolean;
  audioElapsedSeconds: number;
  audioPlaybackRate: number;
  playBotAudio: (audio: string) => void;
  stopBotSpeaking: () => void;
  toggleBotAudioPause: () => void;
  cycleBotAudioRate: () => void;
  hasPendingAudio: boolean;

  handleStopStreaming: () => void;

  // Rating
  // Rating
  handleRateMessage: (messageId: string, action: 'LIKE' | 'DISLIKE', dislikeReason?: string, reasonIds?: string[]) => Promise<void>;

  // File Upload
  handleFileSelect: (files: FileList) => Promise<void>;
  handleRetryUpload: (message: ChatMessage) => Promise<void>;

  // Quote/Reply
  quotedMessage: ChatMessage | null;
  handleQuoteMessage: (message: ChatMessage) => void;
  handleCancelQuote: () => void;
  scrollToQuotedMessage: (targetMessageId: string | number) => Promise<void>;

  markAllAsHistory: () => void;
  connectionStatuses: Record<ChatAgentType, any>;
  needsManualReconnect: Record<ChatAgentType, boolean>;
  manualReconnect: (agentType: ChatAgentType) => void;
  hasCSKHNotification: boolean;
  setHasCSKHNotification: (val: boolean) => void;
  toastMessage: ToastMessage;
  setToastMessage: (toast: ToastMessage) => void;
  chatSessionVersion: number;
  addMessage: (agentType: ChatAgentType, message: ChatMessage) => void;
  updateMessage: (agentType: ChatAgentType, id: string, partial: Partial<ChatMessage>) => void;
  getCurrentChatLog: (agentType: ChatAgentType) => ChatMessage[];
  user: UserInfo | null | undefined;
  reloadActiveAgentHistory: () => Promise<void>;
  clearStoppedMessages: () => void;
  sendChangeModeMessage: (targetAgent: ChatAgentType) => boolean;
  refreshConversationId: () => Promise<number | null>;
  /** POST message session — new conversation. Skip API if thread already empty (unless force). */
  startNewChatSession: (
    typeChat?: ChatAgentType,
    options?: { force?: boolean },
  ) => Promise<string | null>;
  /** GET current message session — enter chatbot. force=true always re-fetches history. */
  loadCurrentChatSession: (
    typeChat?: ChatAgentType,
    options?: { force?: boolean },
  ) => Promise<string | null>;
  /** Select existing session from sidebar — set sessionId + load its messages (optional agent, e.g. AI history) */
  selectChatSession: (sessionId: string, typeChat?: ChatAgentType) => Promise<void>;
}

interface PendingForwardInfo {
  targetAgent: ChatAgentType;
  finalUserQuestion: string | undefined;
  botMessage: ChatMessage;
  botMessageId: string | undefined;
  messageTime: string;
  hasForwardableContent: boolean;
  /** Component from source USER message — Case 2 sendMessage attachments if present */
  attachedComponent?: ChatComponentItem | null;
}

interface PendingNewMessageInfo {
  targetAgent: ChatAgentType;
  contentToSend: string;
  messageCodeMessage: string;
  messageTime: string;
  socketImages: Array<{
    contentType: string;
    imageUrl: string;
    name: string;
    uploadStatus: string;
  }>;
  attachedComponent?: ChatComponentItem | null;
}

export const useChat = (): UseChatReturn => {
  const { t } = useTranslation();
  const [connected, setConnected] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [redirectData, setRedirectData] = useState<RedirectData | null>(null);
  const [quotedMessage, setQuotedMessage] = useState<ChatMessage | null>(null); // State for quoted message
  const chatRef = useRef<HTMLDivElement>(null);
  const stoppedCodeMessagesRef = useRef<Set<string>>(new Set());
  const lastSentCodeMessageRef = useRef<string | null>(null);
  const lastUserCodeMessageRef = useRef<Record<ChatAgentType, string | null>>({
    [ChatAgentType.AI]: null,
    [ChatAgentType.CSKH]: null,
    [ChatAgentType.TAX_QUESTION]: null,
    [ChatAgentType.INSTRUCTION_MANUAL]: null,
    [ChatAgentType.ANALYTICS_QUESTION]: null,
  });
  const ignoreNextBotResponseRef = useRef<Record<ChatAgentType, boolean>>({
    [ChatAgentType.AI]: false,
    [ChatAgentType.CSKH]: false,
    [ChatAgentType.TAX_QUESTION]: false,
    [ChatAgentType.INSTRUCTION_MANUAL]: false,
    [ChatAgentType.ANALYTICS_QUESTION]: false,
  });
  const lastStopStreamingTimeRef = useRef<Record<ChatAgentType, number>>({
    [ChatAgentType.AI]: 0,
    [ChatAgentType.CSKH]: 0,
    [ChatAgentType.TAX_QUESTION]: 0,
    [ChatAgentType.INSTRUCTION_MANUAL]: 0,
    [ChatAgentType.ANALYTICS_QUESTION]: 0,
  });
  const lastSubmitTimeRef = useRef<number>(0);
  const isSubmittingRef = useRef(false);
  const [hasCSKHNotification, setHasCSKHNotification] = useState(false);
  const [toastMessage, setToastMessage] = useState<ToastMessage>(null);
  const [chatSessionVersion, setChatSessionVersion] = useState(0);

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const showToast = useCallback((
    text: string,
    type: 'success' | 'error' | 'info' | 'cskh' = 'error',
    extras?: { title?: string; timeLabel?: string; hideIcon?: boolean },
  ) => {
    setToastMessage({ text, type, id: generateUUID(), ...extras });
  }, []);


  // Monitor network status
  useEffect(() => {
    const handleOnline = () => {
      setToastMessage(null);
    };
    const handleOffline = () => {
      showToast(t('common.noInternetConnection'), 'error');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial state
    if (!navigator.onLine) {
      handleOffline();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [t, showToast]);

  // Geolocation — request once on mount, push to websocket hook when resolved
  const userLocation = useGeoLocation();
  useEffect(() => {
    setUserLocation(userLocation);
  }, [userLocation]);

  // Chat Token
  const { token } = useChatToken(
    (t, c) => handleTokenReceivedRef.current(t, c),
    (next, prev) => hardResetOnTokenChangeRef.current(next, prev),
  );
  const { user } = useGetUserInfo(token);

  // Chat Agent
  const {
    activeAgent,
    handleAgentChange: handleAgentChangeBase,
  } = useChatAgent();

  // Suggestions API Handler (Commands, Quick Functions, Instructions)
  const {
    commands,
    isCommandsLoading,
    quickFunctions,
    quickFunctionSearchList,
    instructions,
    isInstructionsLoading,
    refreshInstructions,
    quickSelectionData,
    setQuickSelectionData,
    handleInstructionClick,
    handleQuickFunctionClick,
    closeQuickSelection
  } = useChatSuggestions({ activeAgent, token });

  // Ref để lưu hàm playBotAudio (vì được define sau WebSocket callback)
  const playBotAudioRef = useRef<((audio: string) => void) | null>(null);

  // Ref để lưu hàm stopBotSpeaking (vì được define sau WebSocket callback)
  const stopBotSpeakingRef = useRef<(() => void) | null>(null);

  // Ref để lưu activeAgent mới nhất cho WebSocket callback
  const activeAgentRef = useRef<ChatAgentType>(activeAgent);
  activeAgentRef.current = activeAgent;

  // Ref để lưu hàm handleAgentChange (vì được define sau WebSocket callback)
  const handleAgentChangeRef = useRef<((newAgent: ChatAgentType) => void) | null>(null);
  const startNewChatSessionRef = useRef<
    (typeChat?: ChatAgentType, options?: { force?: boolean }) => Promise<string | null>
  >(async () => null);
  /** Suggest→AI from another agent: submit after activeAgent becomes AI */
  const pendingSuggestPromptRef = useRef<string | null>(null);
  const currentTokenRef = useRef<string | null>(null);
  const handleTokenReceivedRef = useRef<(token: string, conversationId: number) => void>(() => {});
  const hardResetOnTokenChangeRef = useRef<(nextToken: string, prevToken: string) => void>(() => {});
  const resetForNewTokenRef = useRef<() => void>(() => {});
  /** Bumped on shop/token hard reset — disables local message preserve on reload */
  const accountSwitchEpochRef = useRef(0);
  /** token:agent keys already hydrated this account session — avoid re-fetch on switch/open */
  const sessionLoadedKeysRef = useRef<Set<string>>(new Set());
  /** Agent to force-refetch history on enter (e.g. "Tại đây" from another agent) */
  const forceHistoryOnEnterRef = useRef<ChatAgentType | null>(null);
  const { abort: abortSessionRequest, next: nextSessionRequest } = useAbortableRequest();

  // Per-agent message session ids — survive agent switches without GET /sessions/current overwrite
  const sessionIdByAgentRef = useRef<Record<ChatAgentType, string | null>>({
    [ChatAgentType.AI]: null,
    [ChatAgentType.TAX_QUESTION]: null,
    [ChatAgentType.INSTRUCTION_MANUAL]: null,
    [ChatAgentType.ANALYTICS_QUESTION]: null,
    [ChatAgentType.CSKH]: null,
  });

  const rememberSession = useCallback((agent: ChatAgentType, sessionId: string | null) => {
    sessionIdByAgentRef.current[agent] = sessionId;
  }, []);

  const clearSessionIdByAgent = useCallback(() => {
    (Object.keys(sessionIdByAgentRef.current) as ChatAgentType[]).forEach((agent) => {
      sessionIdByAgentRef.current[agent] = null;
    });
  }, []);

  // Ref để lưu messageId cuối cùng từ server (dùng cho fallback API khi thinking timeout)
  const lastSentMessageIdRef = useRef<string | null>(null);

  // Ref để lưu pending forward info có msgBotFwToNewAgentId (xử lý sau khi history được load từ API)
  const pendingForwardRef = useRef<PendingForwardInfo | null>(null);

  // Ref để lưu pending new message info không có msgBotFwToNewAgentId (xử lý sau khi history được load từ API)
  const pendingNoMsgBotMessageRef = useRef<PendingNewMessageInfo | null>(null);

  // Xử lý side-effects cho mỗi tin nhắn mới nhận được (dùng chung cho socket và fallback API)
  const handleNewMessageEffectsRef = useRef((msg: ChatMessage) => {
    // CHANGE_MODE_SUCCESS → chuyển agent
    if (
      (msg as any).typeChangeFlow === 'CHANGE_MODE_SUCCESS' &&
      (msg as any).targetAgent &&
      Object.values(ChatAgentType).includes((msg as any).targetAgent as ChatAgentType)
    ) {
      const target = (msg as any).targetAgent as ChatAgentType;
      if (stopBotSpeakingRef.current) stopBotSpeakingRef.current();
      setTimeout(() => { if (handleAgentChangeRef.current) handleAgentChangeRef.current(target); }, 500);
    }

    if (msg.senderType !== SenderType.BOT) return;

    // Deeplink web: LINK block với text === 'deeplink_web'
    const deeplinkBlock = (msg.blockContents as any[])?.find(
      (b: any) => b.type === 'LINK' && b.payload?.text === 'deeplink_web' && b.payload?.url
    );
    if (deeplinkBlock) {
      const rawUrl = deeplinkBlock.payload.url as string;
      let routePath = rawUrl;
      try {
        const parsed = new URL(rawUrl);
        routePath = parsed.pathname + parsed.search + parsed.hash;
      } catch { /* rawUrl is already a relative path */ }
      console.log('[Deeplink] NAVIGATE_ROUTE ->', routePath);
      setTimeout(() => {
        safePostMessage({ type: PostMessageType.NAVIGATE_ROUTE, payload: routePath });
        safePostMessage({ type: PostMessageType.CHAT_TOGGLE, isOpen: false });
      }, 100);
    }
  });

  // Chat History
  const {
    chatHistories,
    hasChatHistories,
    cursors,
    previousCursors,
    addMessage,
    updateMessageStream,
    prependMessages,
    appendMessages,
    replaceChatLog,
    setHasChatHistory,
    getCurrentChatLog,
    updateMessage,
    setAllHistories,
    resetAllHistories,
    markAllAsHistory,
    clearStoppedMessages,
  } = useChatHistory();

  useEffect(() => {
    const hasStoredToken = Boolean(sessionStorage.getItem('access_token'));
    const isStandalone = window.self === window.top;

    if (!token && !hasStoredToken && isStandalone) {
      Object.values(ChatAgentType).forEach((agentType) => {
        setHasChatHistory(agentType, false);
      });
    }
  }, [token, setHasChatHistory]);

  // Initial History Loader - load lịch sử cả 5 agents khi lần đầu vào
  const { loadAllHistories, resetForNewToken } = useInitialHistoryLoader({
    onHistoriesLoaded: setAllHistories,
  });
  resetForNewTokenRef.current = resetForNewToken;

  // Chat WebSocket
  const {
    sockets,
    conversationIds,
    connectWebSocket,
    disconnectWebSocket,
    sendMessage,
    sendForwardMessage: sendForwardMessageToSocket,
    setConversationId,
    disconnectOtherSockets,
    connectionStatuses,
    needsManualReconnect,
    manualReconnect,
    clearQueue,
    setUserLocation,
  } = useChatWebSocket(
    (agentType, chatMessage) => {
      // Ignore empty chunks when streaming (fullContent is empty and isFinal is false)
      if (chatMessage.senderType === SenderType.BOT &&
          !chatMessage._isHistory &&
          chatMessage.fullContent === "" &&
          chatMessage.isFinal === false) {
        console.log('[Chat Socket] Ignoring bot chunk with empty fullContent and isFinal=false');
        return;
      }

      // Track last user message codeMessage for this agent
      if (chatMessage.senderType === SenderType.USER) {
        lastUserCodeMessageRef.current[agentType] = chatMessage.codeMessage || null;
      }

      // Correlate bot response with stopped user query
      // NOTE: Skip for history messages — we never want to wipe content from replayed history chunks.
      const parentUserCodeMsg = lastUserCodeMessageRef.current[agentType];
      if (
        chatMessage.senderType === SenderType.BOT &&
        !chatMessage._isHistory &&
        parentUserCodeMsg &&
        stoppedCodeMessagesRef.current.has(parentUserCodeMsg)
      ) {
        console.log('[Chat Socket] Correlated bot response with stopped query:', parentUserCodeMsg);
        chatMessage.text = '';
        chatMessage.fullContent = '';
        chatMessage.blockContents = [];
        chatMessage.isStopped = true;
        chatMessage.isFinal = true;
        if (chatMessage.messageId) {
          const mIdStr = String(chatMessage.messageId);

          // Find and update local placeholder's messageId
          const currentLog = getCurrentChatLog(agentType);
          const placeholder = currentLog.find(
            msg =>
              msg.senderType === SenderType.BOT &&
              msg.isStopped === true &&
              msg.codeMessage === parentUserCodeMsg &&
              (!msg.messageId || msg.messageId.startsWith('bot-stream-') || String(msg.messageId) === 'undefined')
          );
          if (placeholder) {
            console.log('[Chat Socket] Updating placeholder ID to matching server message ID:', mIdStr);
            updateMessage(agentType, placeholder.messageId || placeholder.codeMessage || '', {
              messageId: mIdStr,
            });
          }
        }
      }

      // If we are ignoring the next bot response for this agent, discard it
      if (
        chatMessage.senderType === SenderType.BOT &&
        !chatMessage._isHistory &&
        ignoreNextBotResponseRef.current[agentType]
      ) {
        console.log('[Chat Socket] Ignoring bot chunk because response was stopped/paused for', agentType);
        return;
      }

      // Check if message has been stopped/paused -> Ignore (live chunks only, not history replay)
      const codeMsg = chatMessage.codeMessage ? String(chatMessage.codeMessage) : '';
      if (codeMsg && !chatMessage._isHistory && stoppedCodeMessagesRef.current.has(codeMsg)) {
        console.log('[Chat Socket] Ignoring chunk because message was stopped:', codeMsg);
        return;
      }

      // 1. Luôn tắt thinking khi có tin BOT về (không tính tin từ lịch sử)
      if (chatMessage.senderType === SenderType.BOT && !chatMessage._isHistory) {
        setIsThinking(false);
        // Đảm bảo hàng chờ sạch khi kết nối đang bình thường
        clearQueue(agentType);
      }

      // 1. Handle CSKH new message notification (Toast and Red Dot)
      // Any CSKH bot message (via typeChat or socket source) should show toast everywhere;
      // red dot only when user is not currently on the CSKH tab.
      const isCSKHIncoming = (chatMessage.typeChat === 'CSKH' || agentType === ChatAgentType.CSKH) && !chatMessage._isHistory;
      const currentActiveAgent = activeAgentRef.current;

      if (isCSKHIncoming && chatMessage.senderType === SenderType.BOT) {
        if (currentActiveAgent !== ChatAgentType.CSKH) {
          setHasCSKHNotification(true);
        }
        const rawPreview = stripMarkdown(chatMessage.text || chatMessage.fullContent || '')
          .replace(/!\[image\]\([^)]*\)/g, '')
          .replace(/\n/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        const hasImage =
          /!\[image\]\(/i.test(chatMessage.text || chatMessage.fullContent || '') ||
          !!chatMessage.blockContents?.some(
            (b: any) => b.type?.toUpperCase?.() === 'IMAGES' || b.payload?.imageUrl,
          );
        showToast(rawPreview || (hasImage ? 'Ảnh' : 'Bạn có tin nhắn từ CSKH'), 'cskh', {
          title: 'Chăm sóc khách hàng',
          timeLabel: 'bây giờ',
        });

        // If it's a CSKH message from a non-CSKH socket (e.g., Omi sending via current mode), 
        // we only show notification and don't add it to the other agent's log.
        if (agentType !== ChatAgentType.CSKH) {
          return;
        }
      }

      // 2. Kiểm tra trùng để cập nhật trạng thái queuing cho USER message hoặc stream cho BOT
      const currentHistory = getCurrentChatLog(agentType);

      // Chốt chặn kiểm tra xem ID nhận về từ Server có thực sự hợp lệ không
      const hasValidId = chatMessage.messageId &&
        chatMessage.messageId !== 'undefined' &&
        chatMessage.messageId !== 'null' &&
        chatMessage.messageId !== '';

      // --- USER QUEUING RESOLUTION (Check and resolve user queuing BEFORE duplicate check) ---
      const isQueuingMsg = chatMessage.senderType === SenderType.USER &&
        chatMessage.codeMessage &&
        currentHistory.some(msg => msg.codeMessage === chatMessage.codeMessage);

      if (isQueuingMsg) {
        updateMessage(agentType, chatMessage.codeMessage || '', {
          messageId: chatMessage.messageId,
          isQueuing: false,
          ...(chatMessage.uploadStatus === 'failed' ? { uploadStatus: 'failed' } : {}),
          ...buildUserEchoMergeUpdates(chatMessage),
        });
        // Lưu messageId từ server cho fallback API
        if (chatMessage.messageId) {
          lastSentMessageIdRef.current = String(chatMessage.messageId);
        }
        // Không cần gọi addMessage nữa vì nó đã hiển thị trong UI rồi, chỉ cần update ID
        return;
      }

      const isDuplicate = currentHistory.some(msg => {
        // CHỈ CHECK TRÙNG THEO ID NẾU CẢ HAI ĐỀU CÓ ID HỢP LỆ (Không tính chuỗi 'undefined')
        if (hasValidId && msg.messageId && msg.messageId !== 'undefined' && msg.messageId !== 'null') {
          if (isSimilarId(msg.messageId, chatMessage.messageId)) return true;
        }

        // Same USER text is allowed (repeat quick actions). Match by codeMessage only.
        if (
          chatMessage.senderType === SenderType.USER &&
          msg.senderType === SenderType.USER &&
          chatMessage.codeMessage &&
          msg.codeMessage &&
          chatMessage.codeMessage === msg.codeMessage
        ) {
          return true;
        }

        // TEMP disabled: USER text dedupe blocked repeat quick-action sends.
        // // Check by text + senderType + similar createdAt (within 5 seconds)
        // if (msg.text && chatMessage.text &&
        //   msg.text === chatMessage.text &&
        //   msg.user === chatMessage.user &&
        //   msg.senderType === chatMessage.senderType) {
        //
        //   // Tin nhắn do user gửi queue có thể mất nhiều thời gian hơn để gửi lại
        //   if (chatMessage.senderType === SenderType.USER && (!msg.messageId || !chatMessage.messageId)) {
        //     return true;
        //   }
        //
        //   const timeDiff = Math.abs(
        //     new Date(msg.createdAt || msg.time || 0).getTime() -
        //     new Date(chatMessage.createdAt || chatMessage.time || 0).getTime()
        //   );
        //   if (timeDiff < 2000) {
        //     return true;
        //   }
        // }

        // Bot / other: text + sender + close timestamp (stream chunks / echoes)
        if (
          chatMessage.senderType !== SenderType.USER &&
          msg.text &&
          chatMessage.text &&
          msg.text === chatMessage.text &&
          msg.user === chatMessage.user &&
          msg.senderType === chatMessage.senderType
        ) {
          const timeDiff = Math.abs(
            new Date(msg.createdAt || msg.time || 0).getTime() -
            new Date(chatMessage.createdAt || chatMessage.time || 0).getTime()
          );
          if (timeDiff < 2000) {
            return true;
          }
        }

        return false;
      });

      if (!isDuplicate) {
        // 🔥 BẢO VỆ STREAM: Nếu gói tin của BOT đổ về liên tục (Stream) mà thiếu ID cố định,
        // kiểm tra xem có thể gộp trực tiếp vào tin nhắn BOT cuối cùng trong khung chat không.
        if (chatMessage.senderType === SenderType.BOT && !chatMessage._isHistory && currentHistory.length > 0) {
          const lastMsg = currentHistory[currentHistory.length - 1];
          if (lastMsg && lastMsg.senderType === SenderType.BOT && (!lastMsg.messageId || lastMsg.messageId === 'undefined')) {
            if (lastMsg.isStopped) {
              return;
            }
            let mergedMessage: ChatMessage = {
              ...lastMsg,
              ...chatMessage, // giữ toàn bộ content cũ và cập nhật cái mới
              messageId: hasValidId ? chatMessage.messageId : lastMsg.messageId,
              text: chatMessage.text || lastMsg.text || '',
              blockContents: chatMessage.blockContents && chatMessage.blockContents.some((b: any) => (b.type === 'TEXT' && b.payload?.text) || (b.type !== 'TEXT'))
                ? chatMessage.blockContents
                : lastMsg.blockContents,
              fullContent: (lastMsg.fullContent || '') + (chatMessage.fullContent || ''),
              audio: (lastMsg.audio || '') + (chatMessage.audio || ''),
              audioRawText: (lastMsg.audioRawText || '') + (chatMessage.audioRawText || ''),
            };
            updateMessageStream(agentType, lastMsg.messageId || lastMsg.codeMessage || '', mergedMessage);
            if (chatMessage.audio && playBotAudioRef.current && !mergedMessage._isHistory && !mergedMessage.isStopped) {
              playBotAudioRef.current(chatMessage.audio);
            }
            if (chatMessage.isFinal === true && !mergedMessage._isHistory) {
              handleNewMessageEffectsRef.current(mergedMessage);
              if (mergedMessage.messageId) {
                actionTracker.setLastBotContext({
                  messageIdStr: String(mergedMessage.messageId),
                  flow_id: (mergedMessage as any).flow_id || (mergedMessage as any).flowId,
                  intent: (mergedMessage as any).intent,
                  typeChat: mergedMessage.typeChat || agentType,
                });
              }
            }
            return;
          }
        }

        // Tạo ID tạm thời băm độc nhất theo thời gian thực để triệt tiêu nguy cơ trùng "undefined" rác làm đơ UI
        const safeChatMessage = {
          ...chatMessage,
          messageId: hasValidId ? chatMessage.messageId : `bot-stream-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`
        };

        addMessage(agentType, safeChatMessage);

        if (!safeChatMessage._isHistory) {
          handleNewMessageEffectsRef.current(safeChatMessage);
          if (safeChatMessage.messageId) {
            actionTracker.setLastBotContext({
              messageIdStr: String(safeChatMessage.messageId),
              flow_id: (safeChatMessage as any).flow_id || (safeChatMessage as any).flowId,
              intent: (safeChatMessage as any).intent,
              typeChat: safeChatMessage.typeChat || agentType,
            });
          }
          if (safeChatMessage.audio && playBotAudioRef.current && !safeChatMessage.isStopped) {
            playBotAudioRef.current(safeChatMessage.audio);
          }
        }
      } else {
        // XỬ LÝ KHI TRÙNG TIN NHẮN (STREAM TIẾP TỤC ĐỔ CHỮ)
        // Tìm bản ghi cũ chính xác bằng ID thực sự hợp lệ hoặc codeMessage rõ ràng để tránh bóc nhầm tin rác
        const existingMsg = currentHistory.find(msg => {
          if (hasValidId && msg.messageId && isSimilarId(msg.messageId, chatMessage.messageId)) {
            return true;
          }
          if (chatMessage.codeMessage && msg.codeMessage === chatMessage.codeMessage && msg.senderType === chatMessage.senderType) {
            return true;
          }
          return false;
        });

        if (
          existingMsg &&
          (chatMessage.fullContent !== "" ||
            chatMessage.isFinal === true ||
            (chatMessage.blockContents && chatMessage.blockContents.length > 0) ||
            (chatMessage.buttons && chatMessage.buttons.length > 0) ||
            chatMessage.suggestAgentNotify)
        ) {
          if (existingMsg.isStopped) {
            return;
          }
          if (chatMessage._isHistory) {
            const useLocalText = existingMsg.text && existingMsg.text.length > (chatMessage.text || '').length;
            const mergedHistoryMsg = {
              ...chatMessage,
              text: useLocalText ? existingMsg.text : chatMessage.text,
              fullContent: (existingMsg.fullContent && existingMsg.fullContent.length > (chatMessage.fullContent || '').length)
                ? existingMsg.fullContent
                : chatMessage.fullContent,
              blockContents: useLocalText && existingMsg.blockContents && existingMsg.blockContents.length > 0
                ? existingMsg.blockContents
                : chatMessage.blockContents,
            };

            // Align blockContents with fullContent in the merged message to ensure visual consistency
            mergedHistoryMsg.blockContents = alignBlockContentsWithFullContent(
              mergedHistoryMsg.blockContents,
              mergedHistoryMsg.fullContent
            ) || [];

            updateMessage(agentType, existingMsg.messageId || existingMsg.codeMessage || '', mergedHistoryMsg);
            return;
          }
          let mergedMessage: ChatMessage = {
            ...existingMsg,
            ...chatMessage,
            text: chatMessage.text || existingMsg.text || '',
            blockContents: chatMessage.blockContents && chatMessage.blockContents.some((b: any) => (b.type === 'TEXT' && b.payload?.text) || (b.type !== 'TEXT'))
              ? chatMessage.blockContents
              : existingMsg.blockContents,
            fullContent: (existingMsg.fullContent || '') + (chatMessage.fullContent || ''),
            audio: (existingMsg.audio || '') + (chatMessage.audio || ''),
            audioRawText: (existingMsg.audioRawText || '') + (chatMessage.audioRawText || ''),
          };
          updateMessageStream(agentType, existingMsg.messageId || existingMsg.codeMessage || '', mergedMessage);
          if (chatMessage.audio && playBotAudioRef.current && !mergedMessage._isHistory && !mergedMessage.isStopped) {
            playBotAudioRef.current(chatMessage.audio);
          }
          if (chatMessage.isFinal === true && !mergedMessage._isHistory) {
            handleNewMessageEffectsRef.current(mergedMessage);
          }
        } else if (
          !existingMsg &&
          chatMessage.senderType === SenderType.BOT &&
          chatMessage.isFinal === true &&
          !chatMessage._isHistory
        ) {
          // Gói final bị coi là trùng (text + thời gian) nhưng không khớp được tin cũ
          // → vẫn chốt tin BOT cuối để UI thoát trạng thái đang stream (nút Ngưng → Gửi)
          const lastMsg = currentHistory[currentHistory.length - 1];
          if (lastMsg && lastMsg.senderType === SenderType.BOT && lastMsg.isFinal === false) {
            console.log('[Chat Socket] Final chunk matched as duplicate without target → finalize last bot message');
            updateMessage(agentType, lastMsg.messageId || lastMsg.codeMessage || '', { isFinal: true });
          }
        }
      }
    },
    async (agentType, history) => {
      const nextCursor = history?.data?.nextCursor;
      const historyData = history?.data?.data || [];
      const hasHistoryServer = historyData.length > 0;

      const historyMessages = historyData
        .map((msg: ChatMessageResponse) => convertToChatMessage(msg, agentType, true))
        // Filter theo typeChat: message thuộc agent khác thì không hiện thị ở agent đang load
        .filter((m: ChatMessage) => !m.agentType || m.agentType === agentType) || [];

      // Lấy lịch sử hiện tại từ state
      const currentLog = getCurrentChatLog(agentType);
      const pending = pendingForwardRef.current;
      const pendingNoMsgBot = pendingNoMsgBotMessageRef.current;
      const reloadFromServer =
        !!(pending && pending.targetAgent === agentType && pending.hasForwardableContent);
      // Forward "Tại đây" relies on server history, not local append — always replace.
      const keepLocalHistory = currentLog.length > 0 && !reloadFromServer;

      if (keepLocalHistory) {
        // Đổi agent khi thread đã có tin → không replace bằng server
        console.log('[WS History] skip replace — local history already present', {
          agentType,
          count: currentLog.length,
        });
        setHasChatHistory(agentType, true);
      } else if (reloadFromServer && !hasHistoryServer && currentLog.length > 0) {
        // Empty WS race after "Tại đây" — keep current thread (REST load may already have landed)
        console.log('[WS History] skip empty replace after forward', {
          agentType,
          count: currentLog.length,
        });
        setHasChatHistory(agentType, true);
      } else {
        // Nếu tin nhắn cuối cùng của bot admin chưa có trên server thì giữ nguyên và bỏ trạng thái dừng
        const lastBotMsg = [...currentLog].reverse().find(m => m.senderType === SenderType.BOT);
        let preservedMsg: ChatMessage | null = null;
        if (lastBotMsg) {
          const existsInServerHistory = historyMessages.some((srvMsg) => {
            if (srvMsg.messageId && lastBotMsg.messageId && isSimilarId(srvMsg.messageId, lastBotMsg.messageId)) return true;
            if (srvMsg.id && lastBotMsg.id && isSimilarId(srvMsg.id, lastBotMsg.id)) return true;
            if (srvMsg.codeMessage && lastBotMsg.codeMessage && srvMsg.codeMessage === lastBotMsg.codeMessage) return true;
            return false;
          });

          if (!existsInServerHistory) {
            const lastLogMsg = currentLog[currentLog.length - 1];
            const isLastOrNearLast = lastLogMsg && (
              lastLogMsg.id === lastBotMsg.id ||
              lastLogMsg.messageId === lastBotMsg.messageId ||
              lastLogMsg.codeMessage === lastBotMsg.codeMessage
            );
            if (isLastOrNearLast) {
              preservedMsg = {
                ...lastBotMsg,
                isStopped: false,
              };
            }
          }
        }

        let finalMessages = historyMessages;
        if (preservedMsg) {
          finalMessages = [...historyMessages, preservedMsg];
        }

        replaceChatLog(agentType, finalMessages, nextCursor ?? null, null);

        const shouldHasHistory = hasHistoryServer || finalMessages.length > 0;
        setHasChatHistory(agentType, shouldHasHistory, nextCursor ?? null);
      }

      if (agentType === activeAgentRef.current) {
        setConnected(true);
      }

      const keptLocalAfterHistory =
        keepLocalHistory || (reloadFromServer && !hasHistoryServer && currentLog.length > 0);
      const messagesForDupCheck = keptLocalAfterHistory ? currentLog : historyMessages;

      // Process pending forward after history is loaded (no local append — history is source of truth)
      if (pending && pending.targetAgent === agentType) {
        const {
          finalUserQuestion,
          botMessage,
          hasForwardableContent,
          messageTime,
          attachedComponent,
        } = pending;
        const msgUserFwCodeMessage = botMessage.msgUserFwCodeMessage;
        const socketImages = (botMessage?.msgUserImagesFw || []).map((img, index) => ({
          id: img.geminiFileUri || `fw_img_${Date.now()}_${index}`,
          localUrl: img.imageUrl,
          status: 'success' as const,
        }));
        const components = attachedComponent ? [attachedComponent] : undefined;

        if (hasForwardableContent) {
          // Case 1: Q&A comes from loaded history
          setIsThinking(false);
        } else {
          // Case 2: Forward only user question — keep old local append + auto-reply
          const userQuestionExists = msgUserFwCodeMessage && messagesForDupCheck.some(
            msg => msg.codeMessage === msgUserFwCodeMessage
          );

          const userMsgIndex = msgUserFwCodeMessage ? messagesForDupCheck.findIndex(
            msg => msg.codeMessage === msgUserFwCodeMessage
          ) : -1;
          const hasAnswerInHistory = userMsgIndex !== -1 && messagesForDupCheck.slice(userMsgIndex + 1).some(
            msg => msg.senderType === SenderType.BOT
          );

          if (!userQuestionExists) {
            addMessage(agentType, createChatMessageFromUI({
              text: finalUserQuestion || '',
              user: SenderType.USER,
              isRead: true,
              agentType: agentType,
              time: messageTime,
              msgUserFwCodeMessage: msgUserFwCodeMessage,
              codeMessage: msgUserFwCodeMessage || `fw_${Date.now()}`,
              imageUploadItems: socketImages,
              ...(attachedComponent ? { attachedComponent } : {}),
            }));
          }

          if (!hasAnswerInHistory) {
            const newCodeMessage = msgUserFwCodeMessage || `fw_${Date.now()}`;
            sendMessage(
              agentType,
              finalUserQuestion || "",
              undefined,
              (error) => {
                console.error('[Forward] Error sending queued message:', error);
                setIsThinking(false);
              },
              true,
              undefined,
              undefined,
              newCodeMessage,
              undefined,
              components,
            );

            lastUserCodeMessageRef.current[agentType] = newCodeMessage;
            ignoreNextBotResponseRef.current[agentType] = false;

            setIsThinking(true);
          } else {
            setIsThinking(false);
          }
        }

        pendingForwardRef.current = null;
      }

      if (pendingNoMsgBot && pendingNoMsgBot.targetAgent === agentType) {
        const {
          contentToSend,
          messageCodeMessage,
          messageTime,
          socketImages,
          attachedComponent,
        } = pendingNoMsgBot;

        const messageExists = messagesForDupCheck.some(
          msg => msg.codeMessage === messageCodeMessage
        );

        const userMsgIndex = messagesForDupCheck.findIndex(
          msg => msg.codeMessage === messageCodeMessage
        );
        const hasAnswerInHistory = userMsgIndex !== -1 && messagesForDupCheck.slice(userMsgIndex + 1).some(
          msg => msg.senderType === SenderType.BOT
        );

        if (!messageExists) {
          const imageUploadItems = socketImages.map((img, index) => ({
            id: `fw_new_img_${Date.now()}_${index}`,
            localUrl: img.imageUrl,
            status: 'success' as const,
          }));

          addMessage(agentType, createChatMessageFromUI({
            text: contentToSend,
            user: SenderType.USER,
            isRead: true,
            agentType: agentType,
            time: messageTime,
            codeMessage: messageCodeMessage,
            imageUploadItems,
            ...(attachedComponent ? { attachedComponent } : {}),
          }));
        }

        setIsThinking(!hasAnswerInHistory);
        pendingNoMsgBotMessageRef.current = null;
      }
    },
    (agentType, error) => {
      console.error(`WebSocket error for ${agentType}:`, error);
    },
    (agentType, codeMessage) => {
      // Removed setIsThinking(false) - Keep loading visible until response arrives

      // Trước khi đánh dấu tin mới là queuing, hãy dọn sạch trạng thái queuing của các tin cũ
      // (vì useChatWebSocket đã ghi đè hàng chờ, chỉ gửi 1 tin duy nhất)
      const currentLog = getCurrentChatLog(agentType);
      currentLog.forEach(msg => {
        if (msg.isQueuing && msg.codeMessage !== codeMessage) {
          updateMessage(agentType, msg.codeMessage || '', { isQueuing: false });
        }
      });

      updateMessage(agentType, codeMessage, { isQueuing: true });
    },
    (agentType) => {
      console.log(`[Socket] onClose callback triggered for ${agentType}`);
      if (ignoreNextBotResponseRef.current[agentType]) {
        console.log(`[Socket] Skipping onClose finalize because it was intentionally stopped`);
        return;
      }
      const currentLog = getCurrentChatLog(agentType);
      if (currentLog.length > 0) {
        const lastMsg = currentLog[currentLog.length - 1];
        if (lastMsg && lastMsg.senderType === SenderType.BOT && lastMsg.isFinal === false) {
          console.log(`[Socket] Setting last bot message to isFinal: true because connection closed`);
          updateMessageStream(agentType, lastMsg.messageId || lastMsg.codeMessage || '', {
            ...lastMsg,
            isFinal: true,
          });
        }
      }
    }
  );

  // // Geolocation — triggered by user opening chatbot (CHAT_TOGGLE), push to socket on resolve
  // const userLocation = useGeoLocation();
  // useEffect(() => {
  //   console.log(467, userLocation)
  //   setUserLocation(userLocation);
  // }, [userLocation]);

  // Stop thinking if connection fails
  useEffect(() => {
    if (connectionStatuses[activeAgent] === ConnectionStatus.ERROR || needsManualReconnect[activeAgent]) {
      setIsThinking(false);
    }
  }, [connectionStatuses, activeAgent, needsManualReconnect]);

  // Refs cho fallback effect (tránh dependency thay đổi liên tục gây reset timer)
  const conversationIdsRef = useRef(conversationIds);
  conversationIdsRef.current = conversationIds;
  const addMessageRef = useRef(addMessage);
  addMessageRef.current = addMessage;
  const getCurrentChatLogRef = useRef(getCurrentChatLog);
  getCurrentChatLogRef.current = getCurrentChatLog;

  // Auto-fallback: after 30s thinking, call API to get unread messages
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (isThinking) {
      timeoutId = setTimeout(async () => {
        const currentAgent = activeAgentRef.current;
        console.log('[Thinking Fallback] 30s timeout - fetching unread messages via API...');
        try {
          const token = sessionStorage.getItem('access_token');
          const convId = conversationIdsRef.current[currentAgent];

          if (token && convId) {
            // Ưu tiên ref (server echo), fallback tìm trong chatLog
            let lastMessageId = lastSentMessageIdRef.current || '';

            if (!lastMessageId) {
              // Fallback: tìm message cuối cùng có messageId dạng số (server-assigned)
              const chatLog = getCurrentChatLogRef.current(currentAgent);
              const serverMsg = [...chatLog].reverse().find(m => {
                const id = String(m.messageId || m.id || '');
                // Server IDs là số dài (snowflake), local IDs là UUID hoặc timestamp ngắn
                return id && /^\d{15,}$/.test(id);
              });
              if (serverMsg) {
                lastMessageId = String(serverMsg.messageId || serverMsg.id);
              }
            }

            console.log('[Thinking Fallback] Using lastMessageId:', lastMessageId);

            if (lastMessageId) {
              const response = await chatService.getUnreadMessages(
                token,
                convId,
                lastMessageId,
                currentAgent
              );

              if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
                console.log(`[Thinking Fallback] Got ${response.data.length} unread message(s), appending...`);
                const parsedMessages = chatService.parseHistoryMessages(response.data, currentAgent);
                parsedMessages.forEach(msg => {
                  handleNewMessageEffectsRef.current(msg);
                  const msgId = String(msg.messageId ?? '');
                  if (!msgId || msgId === 'undefined' || msgId === 'null') return;
                  msg._isHistory = false;
                  addMessageRef.current(currentAgent, msg);
                });
              } else {
                console.log('[Thinking Fallback] No unread messages found.');
              }
            } else {
              console.log('[Thinking Fallback] No lastMessageId found, skipping API call.');
            }
          }
        } catch (err) {
          console.error('[Thinking Fallback] Error:', err);
        } finally {
          setIsThinking(false);
        }
      }, 30000); // 30 seconds
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isThinking]);

  // Automatically reset the ignore flag when starting to think/load
  useEffect(() => {
    if (isThinking) {
      ignoreNextBotResponseRef.current[activeAgent] = false;
    }
  }, [isThinking, activeAgent]);

  // Chat Message
  const {
    switchNotification,
    setSwitchNotification,
    handleSwitchAgent: handleSwitchAgentBase,
    handleSkipSwitch: handleSkipSwitchBase,
  } = useChatMessage();

  // Current agent data
  const currentChatLog = getCurrentChatLog(activeAgent);

  const currentConversationId = conversationIds[activeAgent];
  const currentHasChatHistory = hasChatHistories[activeAgent];

  // Handle agent change with WebSocket connection
  const handleAgentChange = useCallback(
    (newAgent: ChatAgentType) => {
      console.log('[useChat handleAgentChange] Changing active agent to:', newAgent, 'current activeAgent:', activeAgent);
      if (stopBotSpeakingRef.current) {
        stopBotSpeakingRef.current();
      }
      // Automatically finalize/stop or delete the current bot stream if switching agents while streaming
      const currentLog = getCurrentChatLog(activeAgent);
      const lastMsg = currentLog.length > 0 ? currentLog[currentLog.length - 1] : null;
      if (lastMsg && lastMsg.senderType === SenderType.BOT) {
        if (!lastMsg.text || !lastMsg.text.trim()) {
          // If the last bot message of the current agent is empty/whitespace, remove it from log
          const newLog = currentLog.slice(0, -1);
          replaceChatLog(activeAgent, newLog, previousCursors[activeAgent] || null, null);
        } else if (lastMsg.isFinal === false) {
          const alignedBlocks = alignBlockContentsWithFullContent(
            lastMsg.blockContents,
            lastMsg.text || lastMsg.fullContent
          );
          updateMessage(activeAgent, lastMsg.messageId || lastMsg.codeMessage || '', {
            isFinal: true,
            isStopped: true,
            blockContents: alignedBlocks,
          });
          if (lastMsg.codeMessage) {
            stoppedCodeMessagesRef.current.add(String(lastMsg.codeMessage));
          }
          window.dispatchEvent(new CustomEvent('skip-typewriter-animation'));
        }
      }

      // Disable buttons/commands in current agent messages before switching
      markAllAsHistory();

      // Prefer map for agent we're leaving — storage may already point at another agent's new session
      const leavingSessionId =
        sessionIdByAgentRef.current[activeAgent] ?? getMessageSessionId();
      rememberSession(activeAgent, leavingSessionId);

      handleAgentChangeBase(newAgent);
      const targetElement = getAgentTabElement(newAgent);
      const targetView = newAgent === ChatAgentType.TAX_QUESTION ? ActionView.TAX_HANDBOOK : ActionView.CHATBOT_AI_ASSISTANT;
      actionTracker.track({
        view: targetView,
        feature: ActionMacroFeature.CHATBOT_V2,
        action: ActionType.TAP,
        element: targetElement,
        extraContext: {
          tag: getAgentTag(newAgent),
          from_agent: activeAgent,
          to_agent: newAgent,
        },
      });
      actionTracker.clearLastBotContext();
      setSwitchNotification(null);
      resetInput();
      setQuotedMessage(null);
      
      // Only show thinking state if we are waiting for a new streamed response from the agent.
      // In Case 1 (hasForwardableContent is true), the QA is forwarded as-is, so no new response is expected.
      const isWaitingForForwardResponse =
        (pendingForwardRef.current?.targetAgent === newAgent && !pendingForwardRef.current?.hasForwardableContent) ||
        pendingNoMsgBotMessageRef.current?.targetAgent === newAgent;

      if (!isWaitingForForwardResponse || newAgent === ChatAgentType.CSKH) {
        setIsThinking(false);
      } else {
        setIsThinking(true);
      }

      ignoreNextBotResponseRef.current[newAgent] = false;

      // Clear CSKH notification dot when entering CSKH tab
      if (newAgent === ChatAgentType.CSKH) {
        setHasCSKHNotification(false);
      }

      const token = sessionStorage.getItem('access_token');
      const conversationId = conversationIds[newAgent];
      const newAgentHistory = getCurrentChatLog(newAgent);

      // Luôn gọi lại API lấy lịch sử khi chuyển agent (bỏ cache "reuse local history"
      // cho thao tác switch thủ công), để agent effect bên dưới force re-fetch.
      forceHistoryOnEnterRef.current = newAgent;
      if (token) {
        sessionLoadedKeysRef.current.delete(`${token}:${newAgent}`);
      }

      // If the last bot message of the new agent is empty/whitespace, remove it from log
      const lastNewAgentMsg = newAgentHistory.length > 0 ? newAgentHistory[newAgentHistory.length - 1] : null;
      if (lastNewAgentMsg && lastNewAgentMsg.senderType === SenderType.BOT && (!lastNewAgentMsg.text || !lastNewAgentMsg.text.trim())) {
        const cleanedNewLog = newAgentHistory.slice(0, -1);
        replaceChatLog(newAgent, cleanedNewLog, previousCursors[newAgent] || null, null);
      }

      // Disconnect all other sockets first (ensure only 1 active connection)
      disconnectOtherSockets(newAgent);

      // Connect WebSocket for new agent (always reconnect to ensure correct agent).
      // History is loaded by the activeAgent session effect / loadSessionMessages —
      // do NOT also GET history via WS on switch (was causing empty race + duplicate search API).
      // Exception: pending "Tại đây" forward still needs onHistoryLoaded to apply server history.
      const needsHistoryForPending =
        pendingForwardRef.current?.targetAgent === newAgent ||
        pendingNoMsgBotMessageRef.current?.targetAgent === newAgent;

      if (token && conversationId) {
        connectWebSocket(newAgent, conversationId, token, false, needsHistoryForPending);
      }

      // Set connected state based on agent history
      if (newAgentHistory.length > 0) {
        setConnected(true);
      } else if (hasChatHistories[newAgent] !== null) {
        setConnected(true);
      } else if (sockets[newAgent] && conversationId) {
        setConnected(true);
      } else {
        setConnected(false);
      }
    },
    [handleAgentChangeBase, conversationIds, sockets, hasChatHistories, connectWebSocket, setSwitchNotification, getCurrentChatLog, disconnectOtherSockets, sendMessage, markAllAsHistory, setHasCSKHNotification, setIsThinking, activeAgent, updateMessage, replaceChatLog, previousCursors, rememberSession]
  );

  // Update ref cho handleAgentChange (để onMessage callback dùng được)
  useEffect(() => {
    handleAgentChangeRef.current = handleAgentChange;
  }, [handleAgentChange]);

  // Handle chat submit
  const handleChatSubmit = useCallback(
    async (
      message: string,
      buttonId?: number,
      isHidden?: boolean,
      images?: Array<{
        contentType: string;
        imageUrl: string;
        name: string;
        uploadStatus: string;
      }>,
      component?: import('../types/chat-component').ChatComponentItem | null,
      meta?: SocketMessageMeta,
    ) => {
      console.log(1233, message)
      if ((!message.trim() && !images?.length && !component) || isSubmitting || isSubmittingRef.current || !currentConversationId) return;

      lastSubmitTimeRef.current = Date.now();
      isSubmittingRef.current = true;
      setIsSubmitting(true);

      const isQuickButton = buttonId !== undefined;
      const currentView = activeAgent === ChatAgentType.TAX_QUESTION ? ActionView.TAX_HANDBOOK : ActionView.CHATBOT_AI_ASSISTANT;
      actionTracker.track({
        view: currentView,
        feature: ActionMacroFeature.CHATBOT_V2,
        action: ActionType.TAP,
        element: isQuickButton ? `btn_action_${buttonId}` : ActionElement.BTN_SEND,
        extraContext: {
          tag: getAgentTag(activeAgent),
          query: message.trim(),
          user_message: message.trim(),
          prompt_length: message.trim().length,
          typeChat: activeAgent,
          session_chat_id: currentConversationId,
          buttonId,
          has_images: !!(images && images.length > 0),
          has_component: !!component,
        },
      });


      // Delay sending message if the stream was just paused/stopped to allow websocket/session cleanup
      const timeSinceLastStop = Date.now() - lastStopStreamingTimeRef.current[activeAgent];
      if (timeSinceLastStop < 1000) {
        await new Promise(resolve => setTimeout(resolve, 1000 - timeSinceLastStop));
      }

      // Reset ignore flag since we are sending a new message
      ignoreNextBotResponseRef.current[activeAgent] = false;

      // Always stop the previous bot audio when sending a new message
      if (stopBotSpeakingRef.current) {
        stopBotSpeakingRef.current();
      }

      // Reconnect WebSocket if it was disconnected (e.g. after pausing)
      const isSocketConnected = sockets[activeAgent]?.readyState === WebSocket.OPEN;
      const isSocketConnecting = sockets[activeAgent]?.readyState === WebSocket.CONNECTING;
      if (!isSocketConnected && !isSocketConnecting && currentConversationId) {
        const activeToken = token || sessionStorage.getItem('access_token');
        if (activeToken) {
          console.log(`[Chat] Reconnecting socket for ${activeAgent} before sending message`);
          connectWebSocket(activeAgent, currentConversationId, activeToken);
        }
      }

      // Automatically finalize/stop the previous bot stream if sending a new message while streaming
      const currentLog = getCurrentChatLog(activeAgent);
      const lastMsg = currentLog.length > 0 ? currentLog[currentLog.length - 1] : null;
      if (lastMsg && lastMsg.senderType === SenderType.BOT && lastMsg.isFinal === false) {
        const alignedBlocks = alignBlockContentsWithFullContent(
          lastMsg.blockContents,
          lastMsg.text || lastMsg.fullContent
        );
        updateMessage(activeAgent, lastMsg.messageId || lastMsg.codeMessage || '', {
          isFinal: true,
          isStopped: true,
          blockContents: alignedBlocks,
        });
        if (lastMsg.codeMessage) {
          stoppedCodeMessagesRef.current.add(String(lastMsg.codeMessage));
        }
        window.dispatchEvent(new CustomEvent('skip-typewriter-animation'));
      }

      const lowerMessage = message.trim().toLowerCase();
      const isTaxHandbook = lowerMessage === 'sổ tay thuế';
      const isTax60Days = lowerMessage === '60 ngày xóa bỏ thuế khoán' || lowerMessage === '60 ngày xoá bỏ thuế khoán';
      const isSilentRedirect = (isTaxHandbook || isTax60Days) && activeAgent !== ChatAgentType.CSKH;

      if (isSilentRedirect) {
        // Try to find the command in current agent configs to extract PDF deeplink
        let matchedCommand = commands.find((cmd: any) =>
          cmd.name.toLowerCase() === lowerMessage ||
          (isTax60Days && cmd.name.toLowerCase() === '60 ngày xóa bỏ thuế khoán')
        );

        // Fallback: If current agent doesn't have it, fetch from TAX_QUESTION agent
        let taxCommands: any[] = [];
        if (!matchedCommand) {
          try {
            const token = sessionStorage.getItem('access_token');
            if (token) {
              const res = await chatService.getDefaultCommands(token, ChatAgentType.TAX_QUESTION);
              const taxData = res?.data?.data || res?.data || res;
              if (Array.isArray(taxData)) {
                taxCommands = taxData;
                matchedCommand = taxCommands.find((cmd: any) =>
                  cmd.name.toLowerCase() === lowerMessage ||
                  (isTax60Days && cmd.name.toLowerCase() === '60 ngày xóa bỏ thuế khoán')
                );
              }
            }
          } catch (err) {
            console.error('[Chat] Failed to fetch global tax commands fallback:', err);
          }
        }

        if (matchedCommand && matchedCommand.type === 'REDIRECT') {
          const redirectData = extractPdfRedirectData(matchedCommand);
          if (redirectData) {
            setRedirectData(redirectData);
          }
        } else if (isTaxHandbook) {
          // Agent Thuế trả từng tài liệu là 1 command PDF (deeplink_web) thay cho command cha
          // "Sổ tay thuế" có params.taxs → gom lại để giữ màn "Tra cứu sổ tay thuế" như cũ
          const items = taxCommands
            .map((cmd: any) => ({ title: cmd.name, uri: extractDirectPdfUrl(cmd) }))
            .filter((item): item is PdfItem => !!item.uri);
          if (items.length > 0) {
            setRedirectData({ type: 'tax_handbook', items, commandName: message.trim() });
          }
        }
      }

      const currentTime = new Date().toISOString();

      // Generate codeMessage for this message - used in socket AND local message
      const messageCodeMessage = generateUUID();
      lastSentCodeMessageRef.current = messageCodeMessage;
      lastUserCodeMessageRef.current[activeAgent] = messageCodeMessage;

      // Check if this is a quote reply (CSKH)
      const isQuoteReply = !!(quotedMessage && activeAgent === ChatAgentType.CSKH);
      
      // Look up the freshest message from history to get the resolved messageId
      let resolvedQuotedMessage = quotedMessage;
      if (isQuoteReply && quotedMessage) {
        const history = getCurrentChatLog(activeAgent);
        const freshMsg = history.find(m => 
          (quotedMessage.codeMessage && m.codeMessage === quotedMessage.codeMessage) ||
          (quotedMessage.id && m.id === quotedMessage.id) ||
          (quotedMessage.messageId && m.messageId === quotedMessage.messageId)
        );
        if (freshMsg) {
          resolvedQuotedMessage = freshMsg;
        }
      }

      let replyToMessageId = isQuoteReply ? (resolvedQuotedMessage?.messageId || resolvedQuotedMessage?.id) : undefined;
      const replyToCodeMessage = isQuoteReply ? resolvedQuotedMessage?.codeMessage : undefined;

      // Filter out temporary local IDs to prevent database constraint errors on the server
      if (replyToMessageId) {
        const idStr = String(replyToMessageId);
        const isTmpId = idStr.length < 15 || idStr.includes('temp') || idStr.includes('img') || idStr.includes('RETRY');
        if (isTmpId) {
          replyToMessageId = undefined;
        }
      }


      try {
        const components = component ? [component] : undefined;

        const messageSent = sendMessage(
          activeAgent,
          message,
          buttonId,
          (error) => {
            console.error('Lỗi khi gửi tin nhắn:', error);
            // Instead of system message, mark original message as failed to show retry button
            updateMessage(activeAgent, messageCodeMessage, {
              uploadStatus: 'failed',
              isQueuing: false
            });
            if (activeAgent !== ChatAgentType.CSKH) {
              setIsThinking(false);
            }
          },
          isHidden,
          replyToMessageId,
          replyToCodeMessage,
          messageCodeMessage,
          images, // Pass images to sendMessage
          components,
          meta,
        );

        if (!messageSent) {
          // If sendMessage returns false, it means it couldn't even queue it
          updateMessage(activeAgent, messageCodeMessage, {
            uploadStatus: 'failed',
            isQueuing: false
          });
          throw new Error('Không thể gửi tin nhắn');
        }

        // Set thinking state if applicable: not silent redirect, and NOT CSKH agent (allow even if isHidden is true).
        if (!isSilentRedirect && activeAgent !== ChatAgentType.CSKH) {
          setIsThinking(true);
        }

        // Only add message to UI if isHidden is not true
        if (!isHidden) {
          const imageItems = (images || []).map((image, index) => ({
            id: `img_legacy_retry_${Date.now()}_${index}`,
            localUrl: image.imageUrl,
            status: 'success' as const,
          }));

          addMessage(activeAgent, createChatMessageFromUI({
            text: message,
            user: SenderType.USER,
            isRead: true,
            agentType: activeAgent,
            time: currentTime,
            codeMessage: messageCodeMessage, // Store codeMessage so it can be quoted later
            // Store quote info in message for UI display
            quotedMessageId: quotedMessage?.messageId || quotedMessage?.id,
            quotedMessageText: quotedMessage?.text || quotedMessage?.suggestAgentNotify,
            quotedMessageUser: quotedMessage?.user,
            imageUploadItems: imageItems,
            attachedComponent: component || undefined,
          }));
        }

        // Clear quoted message after send
        if (quotedMessage) {
          setQuotedMessage(null);
        }


      } catch (error) {
        console.error('Lỗi khi xử lý tin nhắn:', error);
        addMessage(activeAgent, createChatMessageFromUI({
          text: 'Có lỗi xảy ra khi gửi tin nhắn. Đang thử kết nối lại...',
          user: SenderType.SYSTEM,
          isRead: true,
          agentType: activeAgent,
          time: currentTime,
        }));
        setIsThinking(false);

        setTimeout(() => {
          const token = sessionStorage.getItem('access_token');
          if (token && currentConversationId) {
            connectWebSocket(activeAgent, currentConversationId, token);
          }
        }, 2000);
      } finally {
        setIsSubmitting(false);
        isSubmittingRef.current = false;
      }
    },
    [
      isSubmitting,
      currentConversationId,
      activeAgent,
      sendMessage,
      addMessage,
      connectWebSocket,
      quotedMessage,
      commands,
      needsManualReconnect,
      getCurrentChatLog,
      showToast,
      sockets,
      token,
    ]
  );

  // Chat Input - simple input management
  const {
    inputValue,
    onChangeInput,
    resetInput,
  } = useChatInput();

  // Audio Settings
  const { isAudioEnabled, toggleAudio, setAudioEnabled } = useAudioSettings();

  const handleVoiceInputSubmit = useCallback((text: string) => {
    handleChatSubmit(text);
  }, [handleChatSubmit]);

  // Voice Interaction Controller (STT & TTS Conflict Handler)
  const {
    listening,
    isBotSpeaking,
    isAudioPaused,
    isAudioPlayerVisible,
    audioElapsedSeconds,
    audioPlaybackRate,
    hasPendingAudio,
    isSTTSupported,
    playBotAudio,
    stopBotSpeaking,
    toggleBotAudioPause,
    cycleBotAudioRate,
    handleMicrophoneClick,
    stopMicAndDiscard,
    pauseMicAndKeepText,
    cancelMicAndClearText,
    voiceErrorModalType,
    closeVoiceErrorModal,
    retryVoicePermission,
    micDialog,
    closeMicDialog,
    confirmMicDialog,
  } = useVoiceInteractionController({
    isThinking,
    isAudioEnabled,
    onVoiceInputSubmit: handleVoiceInputSubmit,
    onChangeInput,
    resetInput,
    inputValue,
    token,
    silenceTimeoutMs: 5000,
    maxDurationMs: VOICE_CAPTURE_MAX_DURATION_MS,
    onMicrophoneError: (msg: string) => {
      if (!msg || msg === 'aborted' || msg === 'abort') return;
      showToast(msg, 'error', { hideIcon: true });
    },
  });

  // Update ref for WebSocket callback
  useEffect(() => {
    playBotAudioRef.current = playBotAudio;
    stopBotSpeakingRef.current = stopBotSpeaking;
  }, [playBotAudio, stopBotSpeaking]);

  // Chat input disabled khi mic đang active hoặc mất mạng
  const isChatInputDisabled = listening || !window.navigator.onLine;

  // Handle command click - onboard PROMPT map (3.1 / 3.8), then BUSINESS vs REDIRECT
  const handleCommandClick = useCallback(
    (
      commandName: string,
      commandId?: number,
      options?: { actionType?: string; actionValue?: string },
    ) => {
      if (options?.actionType === 'DEEPLINK' && options.actionValue) {
        window.open(options.actionValue, '_blank', 'noopener,noreferrer');
        return;
      }

      // Suggest → hỏi đáp from another agent: new AI session then auto-submit (case 6)
      if (options?.actionType === 'CHATBOT' && options.actionValue) {
        const prompt = options.actionValue;
        if (activeAgent !== ChatAgentType.AI) {
          void (async () => {
            rememberSession(activeAgent, getMessageSessionId());
            await startNewChatSessionRef.current(ChatAgentType.AI);
            pendingSuggestPromptRef.current = prompt;
            handleAgentChangeRef.current?.(ChatAgentType.AI);
          })();
          return;
        }
        onChangeInput(prompt);
        window.setTimeout(() => {
          if (!currentConversationId || isSubmittingRef.current) return;
          handleChatSubmit(prompt);
          onChangeInput('');
        }, 50);
        return;
      }

      const featureAction = ONBOARDING_FEATURE_ACTIONS[commandName];

      // 3.1 / 3.8: đổ prompt vào ô nhập rồi auto-submit
      if (featureAction?.type === 'PROMPT') {
        const prompt = featureAction.prompt;
        onChangeInput(prompt);
        // Cho UI kịp render prompt trong input trước khi gửi
        window.setTimeout(() => {
          if (!currentConversationId || isSubmittingRef.current) return;
          handleChatSubmit(prompt);
          onChangeInput('');
        }, 50);
        return;
      }

      // Find full command object from commands list
      const command = commands.find(
        (cmd: any) => cmd.id === commandId || cmd.name === commandName,
      );

      if (!command) {
        handleChatSubmit(commandName, commandId);
        return;
      }

      const commandType = command.type || 'BUSINESS';

      if (commandType === 'REDIRECT') {
        const directPdfUrl = extractDirectPdfUrl(command);
        if (directPdfUrl) {
          window.open(directPdfUrl, '_blank');
          return;
        }

        const redirectData = extractPdfRedirectData(command);
        if (redirectData) {
          setRedirectData(redirectData);
          return;
        }

        console.warn('[Command] REDIRECT command but no valid deeplink, treating as BUSINESS');
      }

      handleChatSubmit(commandName, commandId);
    },
    [
      commands,
      handleChatSubmit,
      onChangeInput,
      currentConversationId,
      activeAgent,
      rememberSession,
    ]
  );

  // Close redirect overlay
  const closeRedirectOverlay = useCallback(() => {
    setRedirectData(null);
  }, []);

  /** Load messages for a known session id into an agent's thread. */
  const loadSessionMessages = useCallback(
    async (
      agent: ChatAgentType,
      sessionId: string,
      options?: { force?: boolean; keepThinking?: boolean },
    ): Promise<ChatMessage[]> => {
      const accessToken = sessionStorage.getItem('access_token');
      const id = sessionId.trim();
      if (!accessToken || !id) return [];

      const localLog = getCurrentChatLog(agent);
      const rememberedId = sessionIdByAgentRef.current[agent];
      // Only force reload when user actively selects a conversation
      const forceReload = options?.force === true;
      // Skip reload when this agent thread is already on screen for the same session.
      // rememberedId == null: init loadAllHistories filled the log before session id was bound.
      const alreadyShowingThisSession =
        localLog.length > 0 &&
        (rememberedId === id || getMessageSessionId() === id || rememberedId == null);

      setMessageSessionId(id);
      rememberSession(agent, id);
      
      if (!forceReload && alreadyShowingThisSession) {
        console.log('[Session] skip reload — local history already for this session', {
          sessionId: id,
          typeChat: agent,
          count: localLog.length,
        });
        return localLog;
      }

      setQuotedMessage(null);
      if (!options?.keepThinking) {
        setIsThinking(false);
      }

      try {
        console.log('[Session] load messages for session', { sessionId: id, typeChat: agent });
        const res = await chatService.getChatHistory(accessToken, '', agent, id);
        const historyData = res?.data?.data || [];
        const messages = chatService.parseHistoryMessages(historyData, agent, {
          skipAgentFilter: true,
        });
        let nextCursor = res?.data?.nextCursor ?? null;
        if (nextCursor === '') nextCursor = null;

        const hasMore = (res?.data as any)?.hasMore;
        if (hasMore === false) {
          nextCursor = null;
        }

        console.log('[Session] session messages loaded', {
          sessionId: id,
          count: messages.length,
          nextCursor,
        });

        replaceChatLog(agent, messages, nextCursor, null);
        setHasChatHistory(agent, messages.length > 0);
        setChatSessionVersion((prev) => prev + 1);
        return messages;
      } catch (error) {
        console.error('[Session] Failed to load session messages:', error);
        setHasChatHistory(agent, false);
        setChatSessionVersion((prev) => prev + 1);
        return [];
      }
    },
    [replaceChatLog, setHasChatHistory, rememberSession, getCurrentChatLog],
  );

  const startNewChatSession = useCallback(async (
    typeChat: ChatAgentType = activeAgent,
    options?: { force?: boolean },
  ): Promise<string | null> => {
    // Đã là cuộc trò chuyện mới (chưa có tin nhắn) → không POST create lại
    if (!options?.force) {
      const currentLog = getCurrentChatLog(typeChat);
      if (currentLog.length === 0) {
        const existingId =
          sessionIdByAgentRef.current[typeChat] ?? getMessageSessionId();
        console.log('[Session] skip POST create — already empty thread', {
          typeChat,
          existingId,
        });
        if (existingId) {
          rememberSession(typeChat, existingId);
          setMessageSessionId(existingId);
        }
        setQuotedMessage(null);
        setIsThinking(false);
        return existingId;
      }
    }

    const accessToken = sessionStorage.getItem('access_token');
    if (!accessToken) {
      console.warn('[Session] skip POST create — no access_token in sessionStorage');
      return null;
    }

    // Detach in-flight bot stream from UI (same flags as Pause) so chunks
    // don't land on the empty new-chat thread. Do not call handleStopStreaming:
    // that also adds a stopped placeholder / disconnects — not needed here.
    ignoreNextBotResponseRef.current[typeChat] = true;
    const codeToStop =
      lastUserCodeMessageRef.current[typeChat] || lastSentCodeMessageRef.current;
    if (codeToStop) {
      stoppedCodeMessagesRef.current.add(String(codeToStop));
    }
    if (stopBotSpeakingRef.current) {
      stopBotSpeakingRef.current();
    }
    setIsThinking(false);

    try {
      console.log('[Session] POST create message session', { typeChat, force: options?.force });
      const session = await chatService.createMessageSession(accessToken, typeChat);
      if (!session?.id) {
        console.error('[Session] POST create failed — keep current session/thread');
        return null;
      }

      console.log('[Session] new session active', {
        sessionId: session.id,
        name: session.name,
        status: session.status,
      });

      rememberSession(typeChat, session.id);
      // Local UX: clear this agent thread for "cuộc trò chuyện mới"
      replaceChatLog(typeChat, [], null, null);
      setHasChatHistory(typeChat, false);
      setChatSessionVersion((prev) => prev + 1);
      setQuotedMessage(null);

      return session.id;
    } catch (error) {
      console.error('[Session] Failed to create message session:', error);
      return null;
    }
  }, [activeAgent, replaceChatLog, setHasChatHistory, rememberSession, getCurrentChatLog]);

  startNewChatSessionRef.current = startNewChatSession;

  // Abort in-flight GET /sessions/current when token switches
  const loadCurrentChatSession = useCallback(
    async (
      typeChat: ChatAgentType = activeAgent,
      options?: { force?: boolean },
    ): Promise<string | null> => {
      const force = options?.force === true;
      const accessToken = sessionStorage.getItem('access_token');
      if (!accessToken) {
        console.warn('[Session] skip GET current — no access_token');
        return null;
      }

      const controller = nextSessionRequest();

      try {
        console.log('[Session] GET current message session', { typeChat, force });
        const current = await chatService.getCurrentMessageSession(accessToken, typeChat, {
          signal: controller.signal,
        });

        if (controller.signal.aborted) {
          return null;
        }

        if (!current) {
          return null;
        }

        console.log('[Session] current ready', {
          sessionId: current.sessionId || null,
          newSession: current.newSession,
        });

        // newSession=true → tạo session mới; false → load history session hiện tại
        if (current.newSession) {
          // Non-AI: loadAllHistories may already have filled the thread. Force-create
          // would wipe that UI (empty on first switch). Keep local history instead.
          // Exception: force (e.g. "Tại đây") must still create/sync for the new flow.
          if (!force && typeChat !== ChatAgentType.AI) {
            const localLog = getCurrentChatLog(typeChat);
            if (localLog.length > 0) {
              console.log('[Session] newSession but local history present — keep UI', {
                typeChat,
                count: localLog.length,
              });
              setHasChatHistory(typeChat, true);
              return sessionIdByAgentRef.current[typeChat] ?? getMessageSessionId();
            }
          }
          return await startNewChatSession(typeChat, { force: true });
        }

        if (!current.sessionId) {
          return null;
        }

        const localLog = getCurrentChatLog(typeChat);
        const previousSessionId = sessionIdByAgentRef.current[typeChat];
        const sameOrUnboundSession =
          !previousSessionId || previousSessionId === current.sessionId;

        setMessageSessionId(current.sessionId);
        rememberSession(typeChat, current.sessionId);

        // Keep local thread — skip getChatHistory on open / re-entry when session unchanged.
        // force=true (e.g. enter via "Tại đây") always re-fetches.
        if (!force && localLog.length > 0 && sameOrUnboundSession) {
          console.log('[Session] keep local history — skip getChatHistory', {
            typeChat,
            sessionId: current.sessionId,
            count: localLog.length,
          });
          setHasChatHistory(typeChat, true);
          return current.sessionId;
        }

        await loadSessionMessages(typeChat, current.sessionId, { force });
        return current.sessionId;
      } catch (error) {
        if (controller.signal.aborted) {
          return null;
        }
        console.error('[Session] Failed to get current message session:', error);
        return null;
      }
    },
    [
      activeAgent,
      nextSessionRequest,
      rememberSession,
      startNewChatSession,
      loadSessionMessages,
      getCurrentChatLog,
      setHasChatHistory,
    ],
  );

  // After switch to AI from suggest (case 6), submit pending prompt on the new session
  useEffect(() => {
    const prompt = pendingSuggestPromptRef.current;
    if (!prompt || activeAgent !== ChatAgentType.AI) return;

    pendingSuggestPromptRef.current = null;
    onChangeInput(prompt);
    const timer = window.setTimeout(() => {
      if (isSubmittingRef.current) return;
      handleChatSubmit(prompt);
      onChangeInput('');
    }, 80);

    return () => window.clearTimeout(timer);
  }, [activeAgent, handleChatSubmit, onChangeInput]);

  const selectChatSession = useCallback(
    async (sessionId: string, typeChat: ChatAgentType = activeAgent): Promise<void> => {
      const id = sessionId.trim();
      if (!id) {
        console.warn('[Session] skip select — missing sessionId');
        return;
      }

      const accessToken = sessionStorage.getItem('access_token') || currentTokenRef.current;
      // Chặn effect switch-agent GET /current ghi đè session vừa chọn
      if (typeChat !== activeAgent && accessToken) {
        sessionLoadedKeysRef.current.add(`${accessToken}:${typeChat}`);
      }

      // Clear thread immediately so UI leaves welcome / previous session
      replaceChatLog(typeChat, [], null, null);
      setHasChatHistory(typeChat, null);
      // User actively selected a conversation → force reload to avoid stale "alreadyShowing" skip.
      await loadSessionMessages(typeChat, id, { force: true });

      // Chọn lịch sử AI Hỏi đáp từ agent khác → chuyển về hỏi đáp
      if (typeChat !== activeAgent) {
        handleAgentChange(typeChat);
      }
    },
    [
      activeAgent,
      replaceChatLog,
      setHasChatHistory,
      loadSessionMessages,
      handleAgentChange,
    ],
  );

  // Restore remembered session per agent; skip getChatHistory when local thread is warm
  useEffect(() => {
    const accessToken = token || sessionStorage.getItem('access_token');
    if (!accessToken) return;

    const loadKey = `${accessToken}:${activeAgent}`;
    const remembered = sessionIdByAgentRef.current[activeAgent];
    const localLog = getCurrentChatLog(activeAgent);
    const forceRefetch = forceHistoryOnEnterRef.current === activeAgent;

    if (forceRefetch) {
      forceHistoryOnEnterRef.current = null;
      sessionLoadedKeysRef.current.delete(loadKey);
      console.log('[Session] force re-fetch on agent enter', { agent: activeAgent });
    }

    // Already hydrated this agent for this token — only re-bind session id for sends
    if (!forceRefetch && sessionLoadedKeysRef.current.has(loadKey)) {
      if (remembered) {
        setMessageSessionId(remembered);
      }
      return;
    }

    sessionLoadedKeysRef.current.add(loadKey);

    if (remembered) {
      if (!forceRefetch && localLog.length > 0) {
        console.log('[Session] switch agent — reuse local history', {
          agent: activeAgent,
          sessionId: remembered,
          count: localLog.length,
        });
        setMessageSessionId(remembered);
        return;
      }
      console.log('[Session] restore remembered session', {
        agent: activeAgent,
        sessionId: remembered,
        force: forceRefetch,
      });
      void loadSessionMessages(
        activeAgent,
        remembered,
        forceRefetch ? { force: true } : undefined,
      );
      return;
    }

    void loadCurrentChatSession(
      activeAgent,
      forceRefetch ? { force: true } : undefined,
    );
  }, [token, activeAgent, loadCurrentChatSession, loadSessionMessages, getCurrentChatLog]);

  /** Immediate hard reset when parent posts a different ACCESS_TOKEN (before getConversations). */
  const skipPreserveOnReloadRef = useRef(false);

  const hardResetOnTokenChange = useCallback((_nextToken: string, _prevToken: string) => {
    accountSwitchEpochRef.current += 1;
    skipPreserveOnReloadRef.current = true;
    abortSessionRequest();
    resetForNewTokenRef.current();
    resetAllHistories();
    clearChatStorage();
    clearSessionIdByAgent();
    setMessageSessionId(null);
    setQuotedMessage(null);
    setIsThinking(false);
    setChatSessionVersion((prev) => prev + 1);
    sessionLoadedKeysRef.current.clear();
    forceHistoryOnEnterRef.current = null;
  }, [resetAllHistories, abortSessionRequest, clearSessionIdByAgent]);
  hardResetOnTokenChangeRef.current = hardResetOnTokenChange;

  const handleTokenReceived = useCallback(
    (receivedToken: string, conversationId: number) => {
      const isAccountSwitched =
        !!currentTokenRef.current && currentTokenRef.current !== receivedToken;

      if (isAccountSwitched) {
        // Ensure hard reset even if onTokenChanging was skipped (e.g. storage already updated)
        hardResetOnTokenChange(receivedToken, currentTokenRef.current || '');
      }

      currentTokenRef.current = receivedToken;
      Object.values(ChatAgentType).forEach((agentType) => {
        setConversationId(agentType, conversationId);
      });

      const finishConnect = () => {
        connectWebSocket(activeAgent, conversationId, receivedToken);
      };

      if (isAccountSwitched) {
        void loadCurrentChatSession(activeAgent).then(() => {
          sessionLoadedKeysRef.current.add(`${receivedToken}:${activeAgent}`);
          void loadAllHistories(receivedToken, { force: true });
          finishConnect();
        });
      } else {
        void loadAllHistories(receivedToken);
        finishConnect();
      }
    },
    [
      activeAgent,
      setConversationId,
      connectWebSocket,
      loadAllHistories,
      loadCurrentChatSession,
      hardResetOnTokenChange,
    ]
  );
  handleTokenReceivedRef.current = handleTokenReceived;

  const refreshConversationId = useCallback(async (): Promise<number | null> => {
    const accessToken = sessionStorage.getItem('access_token') || currentTokenRef.current;
    if (!accessToken) {
      console.warn('[Conversation] skip refresh — no access_token');
      return null;
    }

    try {
      const response = await chatService.getConversations(accessToken);
      const nextConversationId = response?.data;

      if (!nextConversationId) {
        console.warn('[Conversation] refresh returned empty conversationId');
        return null;
      }

      sessionStorage.setItem('current_conversation_id', String(nextConversationId));
      Object.values(ChatAgentType).forEach((agentType) => {
        setConversationId(agentType, nextConversationId);
      });

      connectWebSocket(activeAgent, nextConversationId, accessToken);
      return nextConversationId;
    } catch (error) {
      console.error('[Conversation] Failed to refresh conversationId:', error);
      return null;
    }
  }, [activeAgent, connectWebSocket, setConversationId]);

  const handleQuickSelectionSelect = useCallback((item: any) => {
    if (quickSelectionData?.type === 'INSTRUCTION') {
      const text =
        typeof item === 'string'
          ? item
          : item.function_name || item.question || item.title || item.name || '';
      if (text) handleChatSubmit(text);
    } else if (quickSelectionData?.type === 'QUICK_FUNCTION') {
      handleChatSubmit(item.name, item.id);
    }
    setQuickSelectionData(null);
  }, [quickSelectionData, handleChatSubmit, setQuickSelectionData]);

  // Handle Rate Message
  const handleRateMessage = useCallback(async (messageId: string, action: 'LIKE' | 'DISLIKE', dislikeReason?: string, reasonIds?: string[]) => {

    const token = sessionStorage.getItem('access_token');
    if (!token) return;

    try {
      await chatService.rateMessage(token, messageId, action, dislikeReason, reasonIds);

      // Update local state so like/dislike persists when switching agents
      updateMessage(activeAgent, messageId, { actionMessage: action });
    } catch (error) {
      console.error('Failed to rate message:', error);
    }
  }, [activeAgent, updateMessage]);

  // Handle switch agent
  const handleSwitchAgent = useCallback(() => {
    if (!switchNotification) return;

    const { targetAgent, question, answer } = switchNotification;

    if (question) {
      addMessage(targetAgent, createChatMessageFromUI({
        text: question,
        user: SenderType.USER,
        isRead: true,
        agentType: targetAgent,
      }));

      if (answer) {
        addMessage(targetAgent, createChatMessageFromUI({
          text: answer,
          user: SenderType.BOT,
          isRead: true,
          agentType: targetAgent,
        }));
      }
    }

    handleSwitchAgentBase(() => {
      handleAgentChange(targetAgent);
    });
  }, [switchNotification, addMessage, handleSwitchAgentBase, handleAgentChange]);

  // Handle skip switch
  const handleSkipSwitch = useCallback(() => {
    handleSkipSwitchBase(() => {
      addMessage(activeAgent, createChatMessageFromUI({
        text: 'Tôi có thể hỗ trợ gì cho bạn tiếp theo?',
        user: SenderType.BOT,
        isRead: true,
        agentType: activeAgent,
      }));
    });
  }, [activeAgent, handleSkipSwitchBase, addMessage]);





  // Embed iframe: transparent document canvas so host page shows in submenu bleed gutter.
  useEffect(() => {
    const docEl = document.documentElement;
    docEl.classList.add('embed-chat-canvas');
    document.body.classList.add('embed-box-visible');
    return () => {
      docEl.classList.remove('embed-chat-canvas');
      document.body.classList.remove('embed-box-visible');
    };
  }, []);

  const loadMoreHistory = useCallback(async () => {
    const cursor = cursors[activeAgent];
    const token = sessionStorage.getItem('access_token');

    if (!cursor || !token || isFetchingMore) return;

    setIsFetchingMore(true);
    try {
      const res = await chatService.getChatHistory(token, cursor, activeAgent);
      if (res?.data?.data) {
        // AI: getChatHistory lấy theo session hiện tại → giữ cả tin của tag con (vd. TAX_QUESTION)
        const isSessionScoped = activeAgent === ChatAgentType.AI && !!getMessageSessionId();
        const messages = chatService.parseHistoryMessages(res.data.data, activeAgent, {
          skipAgentFilter: isSessionScoped,
        });
        let nextCursor = res.data.nextCursor ?? null;

        // Prevent infinite loop: if nextCursor is the same as current cursor, stop
        if (nextCursor === cursor) {
          nextCursor = null;
        }

        // Also check hasMore / hasPrev flags
        const hasMore = (res.data as any).hasMore ?? (res.data as any).hasPrev;
        if (hasMore === false) {
          nextCursor = null;
        }

        prependMessages(activeAgent, messages, nextCursor);
      }
    } catch (error) {
      console.error('Failed to load more history:', error);
    } finally {
      setIsFetchingMore(false);
    }
  }, [activeAgent, cursors, isFetchingMore, prependMessages]);

  // Load newer CSKH history (scroll down after page-index jump)
  const loadNewerHistory = useCallback(async () => {
    if (activeAgent !== ChatAgentType.CSKH) return;
    const prevCursor = previousCursors[activeAgent];
    const token = sessionStorage.getItem('access_token');

    if (!prevCursor || !token || isFetchingMore) return;

    setIsFetchingMore(true);
    try {
      const res = await chatService.getChatHistoryCSKHNewer(token, prevCursor);
      if (res?.data?.data) {
        const messages = chatService.parseHistoryMessages(res.data.data, activeAgent);
        let newPrevCursor = (res?.data as any)?.previousCursor ?? null;

        // Check hasPrev flag
        const hasPrev = (res?.data as any)?.hasPrev;
        if (hasPrev === false) {
          newPrevCursor = null;
        }

        // Prevent infinite loop
        if (newPrevCursor === prevCursor) {
          newPrevCursor = null;
        }

        appendMessages(activeAgent, messages, newPrevCursor);
      }
    } catch (error) {
      console.error('Failed to load newer history:', error);
    } finally {
      setIsFetchingMore(false);
    }
  }, [activeAgent, previousCursors, isFetchingMore, appendMessages]);

  // Scroll to quoted message in CSKH (with page-index API fallback)
  const scrollToQuotedMessage = useCallback(async (targetMessageId: string | number) => {
    if (activeAgent !== ChatAgentType.CSKH) return;

    // First try to find in DOM
    const element = document.getElementById(`message-${targetMessageId}`)
      || document.querySelector(`[data-message-id="${targetMessageId}"]`)
      || document.querySelector(`[data-id="${targetMessageId}"]`);

    if (element instanceof HTMLElement) {
      // Found in DOM, just scroll
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('quote-highlight-zoom');
      setTimeout(() => {
        element.classList.remove('quote-highlight-zoom');
      }, 1200);
      return;
    }

    // Not found in DOM → call page-index API
    const token = sessionStorage.getItem('access_token');
    if (!token) return;

    try {
      const res = await chatService.getMessagePageIndex(token, targetMessageId);

      if (res?.data?.content) {
        const messages = chatService.parseHistoryMessages(res.data.content, ChatAgentType.CSKH);
        const nextCursor = res.data.nextCursor ?? null;
        const prevCursor = res.data.previousCursor ?? null;

        // Replace entire CSKH chatLog with this page
        replaceChatLog(ChatAgentType.CSKH, messages, nextCursor, prevCursor);

        // After React re-renders, scroll to the target message
        setTimeout(() => {
          const el = document.getElementById(`message-${targetMessageId}`)
            || document.querySelector(`[data-message-id="${targetMessageId}"]`)
            || document.querySelector(`[data-id="${targetMessageId}"]`);

          if (el instanceof HTMLElement) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('quote-highlight-zoom');
            setTimeout(() => {
              el.classList.remove('quote-highlight-zoom');
            }, 1200);
          }
        }, 300);
      }
    } catch (error) {
      console.error('[scrollToQuoted] Failed to load page-index:', error);
    }
  }, [activeAgent, replaceChatLog]);

  const reloadActiveAgentHistory = useCallback(async () => {
    const token = sessionStorage.getItem('access_token') || currentTokenRef.current;
    if (!token) return;

    try {
      const res = await chatService.getChatHistory(token, '', activeAgent);
      if (res?.data?.data) {
        const historyData = res.data.data;
        const messages = chatService.parseHistoryMessages(historyData, activeAgent).map(msg => 
          msg.isStopped ? { ...msg, isStopped: false } : msg
        );
        let nextCursor = res.data.nextCursor ?? null;
        const previousCursor = (res.data as any).previousCursor ?? null;

        if (nextCursor === '') nextCursor = null;

        const skipPreserve = skipPreserveOnReloadRef.current;
        let preservedMsg: ChatMessage | null = null;

        if (!skipPreserve) {
          const currentLog = getCurrentChatLog(activeAgent);
          const lastBotMsg = [...currentLog].reverse().find(m => m.senderType === SenderType.BOT);
          if (lastBotMsg) {
            const existsInServerHistory = historyData.some((srvMsg: any) => {
              if (srvMsg.messageId && lastBotMsg.messageId && isSimilarId(srvMsg.messageId, lastBotMsg.messageId)) return true;
              if (srvMsg.id && lastBotMsg.id && isSimilarId(srvMsg.id, lastBotMsg.id)) return true;
              if (srvMsg.codeMessage && lastBotMsg.codeMessage && srvMsg.codeMessage === lastBotMsg.codeMessage) return true;
              return false;
            });

            if (!existsInServerHistory) {
              const lastLogMsg = currentLog[currentLog.length - 1];
              const isLastOrNearLast = lastLogMsg && (
                lastLogMsg.id === lastBotMsg.id || 
                lastLogMsg.messageId === lastBotMsg.messageId ||
                lastLogMsg.codeMessage === lastBotMsg.codeMessage
              );
              if (isLastOrNearLast) {
                preservedMsg = {
                  ...lastBotMsg,
                  isStopped: false,
                };
              }
            }
          }
        }

        let finalMessages = messages;
        if (preservedMsg) {
          finalMessages = [...messages, preservedMsg];
        }

        replaceChatLog(activeAgent, finalMessages, nextCursor, previousCursor);
        setHasChatHistory(activeAgent, finalMessages.length > 0, nextCursor);
        skipPreserveOnReloadRef.current = false;
      }
    } catch (error) {
      console.error('Failed to reload history for active agent:', error);
    }
  }, [activeAgent, replaceChatLog, setHasChatHistory, getCurrentChatLog]);

  const agentConfig = AGENT_CONFIGS[activeAgent] || AGENT_CONFIGS[ChatAgentType.AI];

  // Handle forward to another agent from "Tại đây" button
  // Send socket to persist on backend, switch agent, then load history (no local append).
  const handleForwardToAgent = useCallback((
    targetAgent: ChatAgentType,
    userQuestion: string | undefined,
    botMessage: ChatMessage | undefined,
    botMessageId?: string
  ) => {
    void (async () => {
    if (stopBotSpeakingRef.current) {
      stopBotSpeakingRef.current();
    }
    if (!targetAgent || !botMessage) {
      console.warn('[Forward] Missing targetAgent or botMessage, aborting');
      return;
    }

    const currentConversationId = conversationIds[activeAgent];
    const currentSocket = sockets[activeAgent];
    const token = sessionStorage.getItem('access_token');

    // Check if blockContents has valid content (not empty text)
    // Case: blockContents = [] → false
    // Case: blockContents = [{ type: "TEXT", payload: { text: "" } }] → false
    // Case: blockContents = [{ type: "TEXT", payload: { text: "some content" } }] → true
    // Case: blockContents = [{ type: "ORDER_INFO", ... }] → true
    // Case: blockContents = [{ type: "PRODUCT_LIST", ... }] → true
    const hasBlockContents = botMessage?.blockContents &&
      botMessage.blockContents.length > 0 &&
      botMessage.blockContents.some((block: any) =>
        // TEXT block with non-empty text
        (block.type === 'TEXT' && block.payload?.text && block.payload.text.trim() !== '') ||
        // Non-TEXT blocks (ORDER_INFO, PRODUCT_LIST, etc.) always count as valid content
        (block.type !== 'TEXT')
      );

    // Also consider message text or suggestAgentNotify as valid content to forward
    const hasForwardableContent = (
      hasBlockContents ||
      (botMessage?.text && botMessage.text.trim() !== '') ||
      (botMessage?.suggestAgentNotify && botMessage.suggestAgentNotify.trim() !== '')
    );

    // Determine user question to forward (+ optional component from last USER msg)
    let finalUserQuestion = userQuestion || botMessage.msgUserFwToNewAgent;
    let attachedComponent: ChatComponentItem | null | undefined;
    {
      const currentHistory = getCurrentChatLog(activeAgent);
      const lastUserMsg = [...currentHistory].reverse().find(msg => msg.user === SenderType.USER);
      if (!finalUserQuestion && lastUserMsg) {
        finalUserQuestion = lastUserMsg.text;
      }
      if (lastUserMsg?.attachedComponent) {
        attachedComponent = lastUserMsg.attachedComponent;
      }
    }

    // Prepare socket message data
    const typeChangeFlow = botMessage.typeChangeFlow || 'FLOW_AGENT_PENDING';
    const msgUserFwToNewAgent = finalUserQuestion;
    const msgBotFwToNewAgentId = botMessage.msgBotFwToNewAgentId;
    const msgUserFwCodeMessage = botMessage.msgUserFwCodeMessage;
    const msgUserImagesFw = botMessage.msgUserImagesFw;
    // Chỉ forward attachment sang agent "Hỏi đáp" (AI) — cả socket và tin nhắn local ở tab mới
    // đều không được mang attachment sang các agent khác.
    const forwardableAttachedComponent = targetAgent === ChatAgentType.AI ? attachedComponent : undefined;
    const msgUserAttachmentsFw = forwardableAttachedComponent
      ? buildComponentAttachments(forwardableAttachedComponent)
      : undefined;
    const components = forwardableAttachedComponent ? [forwardableAttachedComponent] : undefined;

    // Prepare message time (use bot message time or current time)
    const messageTime = botMessage?.createdAt || botMessage?.time || new Date().toISOString();

    // Bind leaving agent first so we don't overwrite its session with the target's.
    const leavingSessionId =
      sessionIdByAgentRef.current[activeAgent] ?? getMessageSessionId();
    rememberSession(activeAgent, leavingSessionId);

    // Target conversation only — never fall back to storage (still the source agent).
    let targetSessionId: string | null = sessionIdByAgentRef.current[targetAgent];
    if (targetAgent === ChatAgentType.AI) {
      const aiLog = getCurrentChatLog(ChatAgentType.AI);
      if (!targetSessionId && aiLog.length === 0) {
        console.log('[Forward] empty AI thread — create session before WS');
        targetSessionId = await startNewChatSession(targetAgent, { force: true });
      }
    }
    if (targetSessionId) {
      setMessageSessionId(targetSessionId);
      rememberSession(targetAgent, targetSessionId);
    }
    // Entering via "Tại đây" — always force re-fetch target agent history
    forceHistoryOnEnterRef.current = targetAgent;
    if (token) {
      sessionLoadedKeysRef.current.delete(`${token}:${targetAgent}`);
    }
    console.log('[Forward] target session for WS', {
      targetAgent,
      targetSessionId,
      sourceAgent: activeAgent,
    });

    if (msgBotFwToNewAgentId) {
      // Send socket message (ngầm) to notify backend about forward
      const sendSocketMessage = () => {
        if (!currentSocket || !currentConversationId || currentSocket.readyState !== WebSocket.OPEN) {
          console.warn('[Forward] Socket not ready');
          return false;
        }

        return sendForwardMessageToSocket(
          activeAgent,
          typeChangeFlow,
          targetAgent,
          msgUserFwToNewAgent,
          msgBotFwToNewAgentId,
          msgUserFwCodeMessage,
          undefined,
          msgUserImagesFw,
          targetSessionId,
          msgUserAttachmentsFw,
        );
      };

      // Try to send socket message (hidden) to notify backend about forward
      const socketSent = sendSocketMessage();
      if (!socketSent && token && currentConversationId) {
        if (!currentSocket) {
          connectWebSocket(activeAgent, currentConversationId, token);
        }
        // Retry after connection
        setTimeout(() => {
          sendSocketMessage();
        }, 500);
      }

      // Save pending forward so history load (WS/REST) can apply server messages — no local append
      pendingForwardRef.current = {
        targetAgent,
        finalUserQuestion,
        botMessage,
        botMessageId,
        messageTime,
        hasForwardableContent: !!hasForwardableContent,
        ...(forwardableAttachedComponent ? { attachedComponent: forwardableAttachedComponent } : {}),
      };

      // Đánh dấu các message thuộc luồng forward này để khi load lại lịch sử ở targetAgent,
      // chúng luôn được gán agentType = targetAgent dù typeChat gốc là agent cũ.
      // Tự dọn sau vài giây (đủ cho các lần retry load lịch sử) để tránh registry bị stale.
      forwardOverrideRegistry.targetAgent = targetAgent;
      forwardOverrideRegistry.ids = new Set(
        [msgBotFwToNewAgentId, msgUserFwCodeMessage]
          .filter((v): v is string => !!v)
          .map(String)
      );
      setTimeout(() => {
        if (forwardOverrideRegistry.targetAgent === targetAgent) {
          forwardOverrideRegistry.targetAgent = null;
          forwardOverrideRegistry.ids = new Set();
        }
      }, 5000);

      // Only set thinking if we are waiting for a new response (not Case 1 where content is already forwardable)
      if (!hasForwardableContent) {
        setIsThinking(true);
      }
    } else {
      // Logic gửi thẳng một tin nhắn mới y hệt như nhập tay (cho sang tab mới)
      // Nếu msgUserFwToNewAgent không có, fallback qua finalUserQuestion (hoặc chuỗi cứng nếu không có gì)
      // const contentToSend = botMessage.msgUserFwToNewAgent || finalUserQuestion || "Chuyển đổi trợ lý";
      const contentToSend = botMessage.msgUserFwToNewAgent || finalUserQuestion || '';
      const messageCodeMessage = `fw_new_${Date.now()}`;

      // Chỉ lưu queue bên tab mới, giả lập việc type vào input và gửi
      const socketImages = (botMessage?.msgUserImagesFw || []).map((img, index) => ({
        contentType: img.contentType,
        imageUrl: img.imageUrl,
        name: img.imageUrl.split('/').pop()?.replace(/\.[^.]+$/, '') || `image-${index + 1}`,
        uploadStatus: img.uploadStatus,
      }));

      sendMessage(
        targetAgent,
        contentToSend,
        undefined,
        (error) => { console.error('Forward failed', error) },
        false,
        undefined,
        undefined,
        messageCodeMessage,
        socketImages,
        components,
      );

      lastUserCodeMessageRef.current[targetAgent] = messageCodeMessage;
      ignoreNextBotResponseRef.current[targetAgent] = false;

      // Save pending so history load can decide thinking — do not append user message locally
      pendingNoMsgBotMessageRef.current = {
        targetAgent,
        contentToSend,
        messageCodeMessage,
        messageTime,
        socketImages,
        ...(forwardableAttachedComponent ? { attachedComponent: forwardableAttachedComponent } : {}),
      };

      // Đánh dấu message vừa gửi để khi load lại lịch sử ở targetAgent luôn gán đúng agentType.
      forwardOverrideRegistry.targetAgent = targetAgent;
      forwardOverrideRegistry.ids = new Set([messageCodeMessage]);
      setTimeout(() => {
        if (forwardOverrideRegistry.targetAgent === targetAgent) {
          forwardOverrideRegistry.targetAgent = null;
          forwardOverrideRegistry.ids = new Set();
        }
      }, 5000);

      setIsThinking(true);
    }

    // Disable button and "Tại đây" in the original message (current agent)
    // This prevents user from clicking again after forwarding
    if (botMessageId) {
      const updates: Partial<ChatMessage> = {};

      // Disable "Tại đây" by removing targetAgent
      if (botMessage.targetAgent) {
        updates.targetAgent = undefined;
      }

      // Disable all buttons by setting enabledColor to null
      if (botMessage.buttons && botMessage.buttons.length > 0) {
        updates.buttons = botMessage.buttons.map((btn: any) => ({
          ...btn,
          enabledColor: null,
        }));
      }

      // Disable follow up questions visually
      if (botMessage.followUpQuestions && botMessage.followUpQuestions.length > 0) {
        updates.isFollowUpDisabled = true;
      }

      if (Object.keys(updates).length > 0) {
        updateMessage(activeAgent, botMessageId, updates);
      }
    }

    handleAgentChange(targetAgent);

    // Case 1: Q&A is on the server — force-load history. Case 2 keeps the old local-append flow.
    // Clear enter-force flag here so the activeAgent effect does not double-fetch.
    if (targetSessionId && hasForwardableContent) {
      forceHistoryOnEnterRef.current = null;
      if (token) {
        sessionLoadedKeysRef.current.add(`${token}:${targetAgent}`);
      }
      const messages = await loadSessionMessages(targetAgent, targetSessionId, {
        force: true,
        keepThinking: false,
      });

      const historyHasForwarded = !!(
        msgUserFwCodeMessage &&
        messages.some((msg) => msg.codeMessage === msgUserFwCodeMessage)
      );
      if (msgBotFwToNewAgentId && !historyHasForwarded) {
        await new Promise((resolve) => setTimeout(resolve, 400));
        await loadSessionMessages(targetAgent, targetSessionId, { force: true, keepThinking: false });
      }
    }
    })();
  }, [
    getCurrentChatLog,
    updateMessage,
    handleAgentChange,
    conversationIds,
    sockets,
    sendForwardMessageToSocket,
    connectWebSocket,
    activeAgent,
    rememberSession,
    startNewChatSession,
    loadSessionMessages,
    sendMessage,
  ]);

  const sendChangeModeMessage = useCallback((targetAgent: ChatAgentType) => {
    const socket = sockets[activeAgent];
    const conversationId = conversationIds[activeAgent];
    if (socket && socket.readyState === WebSocket.OPEN && conversationId) {
      const messageData = {
        content: "",
        conversationId,
        device: "web",
        isHidden: true,
        location: userLocation || "",
        typeChat: activeAgent,
        version: SOCKET_MESSAGE_VERSION,
        stream: true,
        action: "CHANGE_MODE",
        targetAgent: targetAgent
      };
      console.log('[Change Mode Socket] Sending switch message:', messageData);
      socket.send(JSON.stringify(messageData));
      return true;
    }
    return false;
  }, [sockets, conversationIds, activeAgent, userLocation]);

  const onReadyConversation = useCallback(() => {
    setConnected(true);
  }, []);

  // Handle File Select - Show local images instantly, upload each independently
  const handleFileSelect = useCallback(async (files: FileList) => {
    if (files.length > MAX_IMAGES_COUNT) {
      showToast(`Bạn đã chọn đủ tối đa ${MAX_IMAGES_COUNT} ảnh`, 'error');
      return;
    }

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const error = validateFile(file);
      if (error) {
        showToast(error, 'error');
        return;
      }
      validFiles.push(file);
    }

    try {
      const token = sessionStorage.getItem('access_token');
      if (!token) throw new Error('No token');

      // Step 1: Resize all images
      const resizedFiles: File[] = [];
      for (const file of validFiles) {
        const resizedFile = await resizeImage(file);
        resizedFiles.push(resizedFile);
      }

      // Step 2: Convert to base64 for instant display
      const base64Urls = await Promise.all(
        resizedFiles.map(file => {
          return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        })
      );

      // Step 3: Build per-image upload items
      const imageUploadItems: import('../types/chat-agent').ImageUploadItem[] = resizedFiles.map((file, index) => ({
        id: `img_${Date.now()}_${index}`,
        localUrl: base64Urls[index],
        status: 'uploading' as const,
        file: file,
      }));

      // Step 4: Show images immediately with per-image uploading status
      const imageMarkdown = base64Urls.map(url => `![image](${url})`).join('\n');
      const codeMessage = `IMAGE_LOCAL_${Date.now()}`;

      const message = createChatMessageFromUI({
        text: imageMarkdown,
        user: SenderType.USER,
        isRead: true,
        agentType: activeAgent,
        time: new Date().toISOString(),
        codeMessage: codeMessage,
        msgUserFwCodeMessage: codeMessage,
        uploadStatus: 'uploading',
        localFiles: resizedFiles,
        imageUploadItems: imageUploadItems,
      });

      addMessage(activeAgent, message);

      // Step 5: Upload each image independently
      uploadImagesToS3PerImage(imageUploadItems, token, codeMessage, message.messageId || message.id);

    } catch (error) {
      console.error('Failed to prepare images:', error);
      showToast('Có lỗi xảy ra khi chuẩn bị ảnh. Vui lòng thử lại.', 'error');
    }
  }, [activeAgent, addMessage, showToast]);

  // Upload a single image to S3, returns result with s3Url or error
  const uploadSingleImageToS3 = async (
    item: import('../types/chat-agent').ImageUploadItem,
    token: string,
    messageId: string,
    itemIndex: number,
  ): Promise<{ success: boolean; s3Url?: string; fileName?: string; contentType?: string; error?: string }> => {
    try {
      const file = item.file;
      if (!file) throw new Error('No file data');



      const extension = file.name.split('.').pop() || 'jpg';
      const uniqueFileName = `${generateUUID()}.${extension}`;

      // Get pre-signed URL for this single image
      const presignedResponse = await chatService.getPresignedUrls(token, [{
        contentType: file.type,
        fileName: uniqueFileName,
      }]);
      const presignedUrls = presignedResponse?.data || presignedResponse;

      if (!Array.isArray(presignedUrls) || presignedUrls.length === 0) {
        throw new Error('Invalid pre-signed URL response');
      }

      const presignedUrl = presignedUrls[0].presignedUrl || presignedUrls[0];

      // Upload to S3
      await chatService.uploadToS3(presignedUrl, file);

      // Generate S3 URL
      const baseUrl = chatService.getImageBaseUrl();
      const s3Url = `${baseUrl}${uniqueFileName}`;

      // Update this specific image's status to success
      updateMessage(activeAgent, messageId, {
        imageUploadItems: (prev: import('../types/chat-agent').ImageUploadItem[]) => {
          const updated = [...prev];
          updated[itemIndex] = {
            ...updated[itemIndex],
            status: 'success',
            s3Url: s3Url,
            file: updated[itemIndex].file, // Keep file reference
          };
          return updated;
        },
      } as any);

      return {
        success: true,
        s3Url,
        fileName: uniqueFileName,
        contentType: file.type,
      };
    } catch (error) {
      console.error(`[Upload] Image ${itemIndex} failed:`, error);

      // Update this specific image's status to failed
      updateMessage(activeAgent, messageId, {
        imageUploadItems: (prev: import('../types/chat-agent').ImageUploadItem[]) => {
          const updated = [...prev];
          updated[itemIndex] = {
            ...updated[itemIndex],
            status: 'failed',
            error: error instanceof Error ? error.message : 'Upload failed',
          };
          return updated;
        },
      } as any);

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed',
      };
    }
  };

  // Upload images to S3 independently (each image uploads on its own)
  const uploadImagesToS3PerImage = async (
    imageUploadItems: import('../types/chat-agent').ImageUploadItem[],
    token: string,
    codeMessage: string,
    messageId: string,
  ) => {
    // Upload all images concurrently but independently
    const results = await Promise.all(
      imageUploadItems.map((item, index) =>
        uploadSingleImageToS3(item, token, messageId, index)
      )
    );

    // Collect successful uploads for socket message
    const successfulResults = results.filter(r => r.success);
    const failedCount = results.filter(r => !r.success).length;

    // Send socket message with only successful images
    if (successfulResults.length > 0) {
      const socketImages = successfulResults.map(r => ({
        contentType: r.contentType!,
        imageUrl: r.s3Url!,
        name: r.fileName!.replace(/\.[^.]+$/, ''),
        uploadStatus: 'SUCCESS',
      }));

      const conversationId = conversationIds[activeAgent];
      if (conversationId) {
        sendMessage(activeAgent, '', undefined, undefined, undefined, undefined, undefined, codeMessage, socketImages);
        lastUserCodeMessageRef.current[activeAgent] = codeMessage;
        ignoreNextBotResponseRef.current[activeAgent] = false;
      }
    }

    // Update overall message text with S3 URLs for successful images
    // Keep base64 for failed images (they'll be retried)
    const updatedMarkdownParts = results.map((r, i) => {
      if (r.success && r.s3Url) {
        return `![image](${r.s3Url})`;
      }
      return `![image](${imageUploadItems[i].localUrl})`;
    });

    // Derive overall uploadStatus
    const overallStatus: 'uploading' | 'success' | 'failed' =
      failedCount === 0 ? 'success' : 'failed'; // partial failure = 'failed' to show retry

    updateMessage(activeAgent, messageId, {
      uploadStatus: overallStatus,
      text: updatedMarkdownParts.join('\n'),
      ...(overallStatus === 'success' ? { localFiles: undefined } : {}),
    });
  };

  // Handle retry upload - creates a NEW message with only failed images
  const handleRetryUpload = useCallback(async (message: ChatMessage) => {
    const token = sessionStorage.getItem('access_token');
    if (!token) return;

    const items = message.imageUploadItems;

    // Case 1: Per-image retry (new flow with imageUploadItems)
    if (items && items.length > 0) {
      const failedItems = items.filter(item => item.status === 'failed');
      const successItems = items.filter(item => item.status === 'success');

      if (failedItems.length === 0) return;

      const originalMessageId = (message.messageId || message.id).toString();
      if (successItems.length > 0) {
        // Original message keeps successful images only
        const successMarkdown = successItems.map(item => `![image](${item.s3Url || item.localUrl})`).join('\n');
        updateMessage(activeAgent, originalMessageId, {
          uploadStatus: 'success',
          uploadError: undefined,
          text: successMarkdown,
          imageUploadItems: successItems,
          localFiles: undefined,
        });
      } else {
        // All images failed - hide original
        updateMessage(activeAgent, originalMessageId, {
          isHidden: true,
          uploadStatus: 'success',
        });
      }

      // Step 2: Create a NEW message at the bottom with failed images (set to uploading)
      const newImageUploadItems: import('../types/chat-agent').ImageUploadItem[] = failedItems.map((item, index) => ({
        id: `img_retry_${Date.now()}_${index}`,
        localUrl: item.localUrl,
        status: 'uploading' as const,
        file: item.file,
      }));

      const newImageMarkdown = failedItems.map(item => `![image](${item.localUrl})`).join('\n');
      const newCodeMessage = `IMAGE_RETRY_${Date.now()}`;

      const newMessage = createChatMessageFromUI({
        text: newImageMarkdown,
        user: SenderType.USER,
        isRead: true,
        agentType: activeAgent,
        time: new Date().toISOString(),
        codeMessage: newCodeMessage,
        msgUserFwCodeMessage: newCodeMessage,
        uploadStatus: 'uploading',
        localFiles: failedItems.map(item => item.file!).filter(Boolean),
        imageUploadItems: newImageUploadItems,
      });

      addMessage(activeAgent, newMessage);

      // Step 3: Upload the failed images again
      uploadImagesToS3PerImage(
        newImageUploadItems,
        token,
        newCodeMessage,
        newMessage.messageId || newMessage.id
      );

      return;
    }

    // Case 2: Legacy retry (old flow without imageUploadItems or text retry)
    if (!message.localFiles || message.localFiles.length === 0) {
      // Case 3: Pure text retry (no image items, no local files)
      const originalMessageId = (message.messageId || message.id).toString();

      // Step 1: Hide original
      updateMessage(activeAgent, originalMessageId, {
        isHidden: true,
        uploadStatus: 'success',
      });

      // Step 2: Create a NEW message at the bottom
      const newCodeMessage = `TEXT_RETRY_${Date.now()}`;
      const newMessage = createChatMessageFromUI({
        text: message.text,
        user: SenderType.USER,
        agentType: activeAgent,
        isRead: true,
        time: new Date().toISOString(),
        codeMessage: newCodeMessage,
        msgUserFwCodeMessage: newCodeMessage,
        quotedMessageId: message.quotedMessageId,
        quotedMessageText: message.quotedMessageText,
        quotedMessageUser: message.quotedMessageUser,
      });

      addMessage(activeAgent, newMessage);

      // Step 3: Send it
      sendMessage(
        activeAgent,
        message.text,
        undefined, // buttonId
        (error) => {
          console.error('[Retry] Text message failed again:', error);
          updateMessage(activeAgent, newCodeMessage, {
            uploadStatus: 'failed',
            isQueuing: false,
          });
          if (activeAgent !== ChatAgentType.CSKH) {
            setIsThinking(false);
          }
        },
        false, // isHidden
        message.quotedMessageId,
        undefined, // replyToCodeMessage
        newCodeMessage
      );

      lastUserCodeMessageRef.current[activeAgent] = newCodeMessage;
      ignoreNextBotResponseRef.current[activeAgent] = false;

      if (activeAgent !== ChatAgentType.CSKH) {
        setIsThinking(true);
      }
      return;
    }

    // Case 2: Legacy retry with files (move to new flow and move to bottom)
    const originalMessageId = (message.messageId || message.id).toString();
    updateMessage(activeAgent, originalMessageId, {
      isHidden: true,
      uploadStatus: 'success',
    });

    // Build imageUploadItems from localFiles for the new flow
    const base64Urls = await Promise.all(
      message.localFiles.map(file => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      })
    );

    const newItems: import('../types/chat-agent').ImageUploadItem[] = message.localFiles.map((file, index) => ({
      id: `img_legacy_retry_${Date.now()}_${index}`,
      localUrl: base64Urls[index],
      status: 'uploading' as const,
      file: file,
    }));

    // Create NEW message at bottom
    const newCodeMessage = message.msgUserFwCodeMessage || `IMAGE_RETRY_${Date.now()}`;
    const newImageMarkdown = newItems.map(item => `![image](${item.localUrl})`).join('\n');

    const newMessage = createChatMessageFromUI({
      text: newImageMarkdown,
      user: SenderType.USER,
      agentType: activeAgent,
      isRead: true,
      time: new Date().toISOString(),
      codeMessage: newCodeMessage,
      msgUserFwCodeMessage: newCodeMessage,
      uploadStatus: 'uploading',
      localFiles: message.localFiles,
      imageUploadItems: newItems,
      quotedMessageId: message.quotedMessageId,
      quotedMessageText: message.quotedMessageText,
      quotedMessageUser: message.quotedMessageUser,
    });

    addMessage(activeAgent, newMessage);

    uploadImagesToS3PerImage(newItems, token, newCodeMessage, newMessage.messageId || newMessage.id);
  }, [activeAgent, updateMessage, addMessage, sendMessage, uploadImagesToS3PerImage, setIsThinking]);

  const handleStopStreaming = useCallback(async () => {
    // Nếu vừa mới submit tin nhắn chưa đủ 1s, trì hoãn 1s trước khi xử lý logic pause để tiến trình gửi tin & socket hoàn tất
    const timeSinceSubmit = Date.now() - lastSubmitTimeRef.current;
    if (timeSinceSubmit < 1000) {
      await new Promise(resolve => setTimeout(resolve, 1000 - timeSinceSubmit));
    }

    const currentLog = getCurrentChatLog(activeAgent);
    const lastMsg = currentLog.length > 0 ? currentLog[currentLog.length - 1] : null;

    ignoreNextBotResponseRef.current[activeAgent] = true;
    lastStopStreamingTimeRef.current[activeAgent] = Date.now();

    if (isThinking) {
      const stoppedMsg = createChatMessageFromUI({
        text: '',
        user: SenderType.BOT,
        agentType: activeAgent,
        codeMessage: lastSentCodeMessageRef.current || undefined,
      });
      stoppedMsg.isStopped = true;
      stoppedMsg.isFinal = true;
      addMessage(activeAgent, stoppedMsg);
      if (lastSentCodeMessageRef.current) {
        stoppedCodeMessagesRef.current.add(lastSentCodeMessageRef.current);
      }
    } else if (lastMsg && lastMsg.senderType === SenderType.BOT && lastMsg.isFinal === false) {
      const alignedBlocks = alignBlockContentsWithFullContent(
        lastMsg.blockContents,
        lastMsg.text || lastMsg.fullContent
      );
      updateMessage(activeAgent, lastMsg.messageId || lastMsg.codeMessage || '', {
        isFinal: true,
        isStopped: true,
        blockContents: alignedBlocks,
      });
      if (lastMsg.codeMessage) {
        stoppedCodeMessagesRef.current.add(String(lastMsg.codeMessage));
      }
    }
    if (stopBotSpeakingRef.current) {
      stopBotSpeakingRef.current();
    }
    setIsThinking(false);

    // Ngắt kết nối socket cũ khi nhấn dừng/pause
    disconnectWebSocket(activeAgent);

    window.dispatchEvent(new CustomEvent('skip-typewriter-animation'));
  }, [activeAgent, isThinking, getCurrentChatLog, updateMessage, addMessage, disconnectWebSocket]);

  // Handle quote message (reply)
  const handleQuoteMessage = useCallback((message: ChatMessage) => {
    // // Don't allow quoting image-only messages
    // const hasOnlyImages = message.text && message.text.trim().match(/^(\!\[image\]\(.*?\)\s*)+$/);
    // if (hasOnlyImages) {
    //   return;
    // }

    setQuotedMessage(message);
  }, []);

  // Handle cancel quote
  const handleCancelQuote = useCallback(() => {
    setQuotedMessage(null);
  }, []);

  return {
    activeAgent,
    handleAgentChange,
    currentChatLog,
    currentHasChatHistory,
    connected,
    inputValue,
    listening,
    onChangeInput,
    handleMicrophoneClick,
    stopMicAndDiscard,
    handleChatSubmit,
    handleCommandClick,
    handleSwitchAgent,
    handleSkipSwitch,
    switchNotification,
    isThinking,
    setIsThinking,
    handleStopStreaming,
    commands,
    chatRef,
    agentConfig,
    onReadyConversation,
    isSTTSupported,
    micDialog,
    closeMicDialog,
    confirmMicDialog,
    loadMoreHistory,
    loadNewerHistory,
    isFetchingMore,
    handleForwardToAgent,
    redirectData,
    closeRedirectOverlay,
    // Audio
    isAudioEnabled,
    toggleAudio,
    setAudioEnabled,
    isBotSpeaking,
    isAudioPaused,
    isAudioPlayerVisible,
    audioElapsedSeconds,
    audioPlaybackRate,
    playBotAudio,
    stopBotSpeaking,
    toggleBotAudioPause,
    cycleBotAudioRate,
    hasPendingAudio,
    voiceErrorModalType,
    closeVoiceErrorModal,
    retryVoicePermission,
    pauseMicAndKeepText,
    cancelMicAndClearText,
    // Input disabled state
    isChatInputDisabled,
    isCommandsLoading,
    // Quick Selection
    quickSelectionData,
    handleInstructionClick,
    handleQuickFunctionClick,
    closeQuickSelection,
    handleQuickSelectionSelect,
    quickFunctions,
    quickFunctionSearchList,
    instructions,
    isInstructionsLoading,
    refreshInstructions,
    handleRateMessage,
    handleFileSelect,
    handleRetryUpload,
    // Quote
    quotedMessage,
    handleQuoteMessage,
    handleCancelQuote,
    scrollToQuotedMessage,
    markAllAsHistory,
    connectionStatuses,
    needsManualReconnect,
    manualReconnect: (agentType: ChatAgentType) => {
      if (!navigator.onLine) {
        showToast(t('common.noInternetConnection'), 'error');
        return;
      }
      if (agentType === activeAgent && agentType !== ChatAgentType.CSKH) {
        setIsThinking(true);
      }
      manualReconnect(agentType);
    },
    hasCSKHNotification,
    setHasCSKHNotification,
    toastMessage,
    setToastMessage,
    chatSessionVersion,
    addMessage,
    updateMessage,
    getCurrentChatLog,
    user,
    reloadActiveAgentHistory,
    clearStoppedMessages,
    sendChangeModeMessage,
    refreshConversationId,
    startNewChatSession,
    loadCurrentChatSession,
    selectChatSession,
  };
};
